import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Progress,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Spin,
  Alert,
  Typography,
  Tabs,
  Badge,
  Modal,
  Select,
  message,
  Tooltip,
  List,
  Empty,
  Divider,
  Input,
  InputNumber,
  Checkbox,
  Steps,
  Form,
  Drawer,
  Collapse,
} from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  RobotOutlined,
  SaveOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  BulbOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  CheckSquareOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  EditOutlined,
  ArrowRightOutlined,
  SettingOutlined,
  StarOutlined,
  TagOutlined,
  FormOutlined,
  CloseCircleOutlined,
  RollbackOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  ApartmentOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { scopedKey, getActiveAccountId } from '../utils/accountSession';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;
const { TextArea } = Input;
const DEFAULT_ANTHROPIC_MODEL = 'claude-3-5-sonnet-20241022';
const DEFAULT_DQ_AI_SETTINGS = {
  engine: 'openai',
  default_engine: 'openai',
  taxonomy_root_id: null,
  temperature: 0.1,
  parallel_workers: 1,
  openai_api_key: '',
  openai_model: 'gpt-4',
  openai_max_request_tokens: 128000,
  openai_max_response_tokens: 4000,
  anthropic_api_key: '',
  anthropic_model: DEFAULT_ANTHROPIC_MODEL,
  anthropic_max_request_tokens: 200000,
  anthropic_max_response_tokens: 4000,
  gemini_api_key: '',
  gemini_model: 'gemini-pro',
  gemini_max_request_tokens: 32768,
  gemini_max_response_tokens: 2048,
  category_consistency: {
    enabled: true,
    mode: 'strict_family',
    dry_run_only: true,
    min_family_size: 2,
    min_confidence: 0.6,
    variation_dictionary_version: 'v1',
  },
};
const STANDARDIZE_TARGET_FIELD_OPTIONS = [
  { label: 'Name', value: 'name' },
  { label: 'Description', value: 'description' },
  { label: 'Category', value: 'category' },
  { label: 'Manufacturer', value: 'manufacturer_name' },
  { label: 'Manufacturer Part Number', value: 'manufacturer_part_number' },
  { label: 'Attributes', value: 'attributes' },
];

function RecValueEditor({ value, onChange, isAttribute, overridden }) {
  const [addInput, setAddInput] = useState('');
  const isMulti = Array.isArray(value);
  const borderStyle = { borderColor: overridden ? '#fa8c16' : undefined };

  const handleAddVal = () => {
    const v = addInput.trim();
    if (!v) return;
    onChange(isMulti ? [...value, v] : [v]);
    setAddInput('');
  };

  const handleRemoveVal = (idx) => onChange(value.filter((_, i) => i !== idx));

  if (isMulti) {
    return (
      <div style={{ border: '1px solid', borderRadius: 6, padding: '4px 6px', maxWidth: 260, ...borderStyle }}>
        {value.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
            {value.map((v, i) => (
              <Tag key={i} closable onClose={() => handleRemoveVal(i)} style={{ marginRight: 0 }}>
                {String(v)}
              </Tag>
            ))}
          </div>
        )}
        <Space.Compact size="small" style={{ width: '100%' }}>
          <Input
            size="small"
            placeholder="Add value…"
            value={addInput}
            onChange={(e) => setAddInput(e.target.value)}
            onPressEnter={handleAddVal}
          />
          <Button size="small" icon={<PlusOutlined />} onClick={handleAddVal} />
          <Tooltip title="Switch to single value">
            <Button size="small" onClick={() => onChange(value.length > 0 ? String(value[0]) : '')}>1×</Button>
          </Tooltip>
        </Space.Compact>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: 260 }}>
      <Input
        size="small"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter value…"
        style={{ ...borderStyle, flex: 1 }}
      />
      {isAttribute && (
        <Tooltip title="Switch to multi-value (array)">
          <Button size="small" onClick={() => onChange(value ? [String(value)] : [])}>+</Button>
        </Tooltip>
      )}
    </div>
  );
}


const DataQuality = () => {
  const { getAuthHeaders, isAuthenticated, loading: authLoading } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [allFields, setAllFields] = useState({ standard_fields: {}, attribute_fields: {} });
  const [activeTab, setActiveTab] = useState('overview');
  
  // Field selection state
  const [fieldSearch, setFieldSearch] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [clearFieldLoadingMap, setClearFieldLoadingMap] = useState({});
  const [showStandardFields, setShowStandardFields] = useState(true);
  const [showAttributeFields, setShowAttributeFields] = useState(true);
  
  // Field configuration drawer
  const [configDrawerVisible, setConfigDrawerVisible] = useState(false);
  const [configField, setConfigField] = useState(null);
  const [configForm] = Form.useForm();
  const activeAccountId = getActiveAccountId();
  const DEFAULT_PROMPT_STORAGE_KEY = scopedKey('dq_default_ai_prompt', activeAccountId);
  const DQ_AI_SETTINGS_KEY = scopedKey('dq_ai_settings', activeAccountId);
  const DQ_RECOMMENDATIONS_KEY = scopedKey('dq_recommendations', activeAccountId);
  const DQ_FIELD_CONFIGS_KEY = scopedKey('dq_field_configs', activeAccountId);
  const [fieldConfigs, setFieldConfigs] = useState(() => {
    try {
      const stored = localStorage.getItem(DQ_FIELD_CONFIGS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (err) {
      return {};
    }
  }); // { field_key: { custom_prompt, golden_values, additional_fields } }
  const [dataQualityPrompt, setDataQualityPrompt] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptModalVisible, setPromptModalVisible] = useState(false);

  // AI Standardize products (batch)
  const [standardizeModalVisible, setStandardizeModalVisible] = useState(false);
  const [standardizeProductCodes, setStandardizeProductCodes] = useState([]);
  const [standardizeEngine, setStandardizeEngine] = useState('openai');
  const [standardizePrompt, setStandardizePrompt] = useState('');
  const [standardizeTargetFields, setStandardizeTargetFields] = useState([]);
  const [standardizeResults, setStandardizeResults] = useState([]);
  const [standardizeConsistencyReport, setStandardizeConsistencyReport] = useState(null);
  const [standardizeRunning, setStandardizeRunning] = useState(false);
  const [standardizeDryRunRunning, setStandardizeDryRunRunning] = useState(false);
  const [standardizeNeedsReviewOnly, setStandardizeNeedsReviewOnly] = useState(false);
  const [standardizeDebugFirstRecord, setStandardizeDebugFirstRecord] = useState(false);
  const [standardizeExcludeOwnFieldValueSource, setStandardizeExcludeOwnFieldValueSource] = useState(false);
  const [selectedIssueKeys, setSelectedIssueKeys] = useState([]);
  const [selectedIssueRows, setSelectedIssueRows] = useState([]);
  const [summaryStandardizeCodes, setSummaryStandardizeCodes] = useState([]);
  const [standardizeDrawerWidthPct, setStandardizeDrawerWidthPct] = useState(50);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [standardizeDetailLoading, setStandardizeDetailLoading] = useState(false);
  const [standardizeDetailError, setStandardizeDetailError] = useState('');
  const [standardizeDetailProduct, setStandardizeDetailProduct] = useState(null);
  
  // AI Engine settings for Data Quality
  const [dqAiSettings, setDqAiSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(DQ_AI_SETTINGS_KEY);
      return stored ? { ...DEFAULT_DQ_AI_SETTINGS, ...JSON.parse(stored) } : { ...DEFAULT_DQ_AI_SETTINGS };
    } catch (err) {
      return { ...DEFAULT_DQ_AI_SETTINGS };
    }
  });
  const [taxonomyRootOptions, setTaxonomyRootOptions] = useState([]);
  const [aiSettingsModalVisible, setAiSettingsModalVisible] = useState(false);
  const [aiSettingsSaving, setAiSettingsSaving] = useState(false);
  const [anthropicModels, setAnthropicModels] = useState([]);
  const [anthropicModelsLoading, setAnthropicModelsLoading] = useState(false);
  const [testingAiEngine, setTestingAiEngine] = useState(false);
  
  // AI Analysis state
  const [analysisStep, setAnalysisStep] = useState(0); // 0: select, 1: running, 2: results
  const [analysisResults, setAnalysisResults] = useState({}); // { fieldKey: { result, parsed, loading } }
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentField: '' });
  
  // Recommendations state
  const [recommendations, setRecommendations] = useState(() => {
    try {
      const stored = localStorage.getItem(DQ_RECOMMENDATIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      return [];
    }
  });
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [recProductsByKey, setRecProductsByKey] = useState({});
  const [reviewActionLoading, setReviewActionLoading] = useState({});
  const [reviewBulkLoading, setReviewBulkLoading] = useState({});
  const [recGroupOverrides, setRecGroupOverrides] = useState({}); // { recId: string } — edited suggested values per rec
  const [recGroupEditing, setRecGroupEditing] = useState({}); // { recKey: boolean } — which groups are in edit mode
  const [recProductOverrides, setRecProductOverrides] = useState({}); // { `${recKey}__${productCode}`: string }
  
  // Detailed analysis state (for uncategorized values)
  const [detailedAnalysisLoading, setDetailedAnalysisLoading] = useState({});
  
  // Batch analysis state
  const [batchAnalysisState, setBatchAnalysisState] = useState({}); // { fieldKey: { running, currentBatch, totalBatches, cancelled } }
  const batchCancelledRef = React.useRef({}); // Using ref for immediate cancellation check
  const analysisResultsRef = React.useRef(analysisResults);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const [analysisJobStatus, setAnalysisJobStatus] = useState(null);
  const analysisJobProcessedRef = useRef({});
  const analysisJobCompletedRef = useRef(false);

  useEffect(() => {
    analysisResultsRef.current = analysisResults;
  }, [analysisResults]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const stopAllBatchJobs = () => {
    const keys = Object.keys(batchAnalysisState || {});
    if (!keys.length) {
      message.info('No batch jobs to stop');
      return;
    }
    keys.forEach((k) => {
      batchCancelledRef.current[k] = true;
    });
    setBatchAnalysisState((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = { ...(next[k] || {}), running: false, cancelled: true };
      });
      return next;
    });
    message.success('All batch jobs stopped');
  };

  const normalizeAnthropicModel = useCallback((model) => {
    const value = String(model || '').trim();
    if (!value) return DEFAULT_ANTHROPIC_MODEL;
    if (value.toLowerCase().includes('latest')) return DEFAULT_ANTHROPIC_MODEL;
    return value;
  }, []);

  const getAiEngineConfig = () => {
    const aiEngine = dqAiSettings.default_engine || dqAiSettings.engine || 'openai';
    const aiKey =
      aiEngine === 'openai'
        ? dqAiSettings.openai_api_key
        : aiEngine === 'anthropic'
        ? dqAiSettings.anthropic_api_key
        : aiEngine === 'gemini'
        ? dqAiSettings.gemini_api_key
        : '';
    const aiModel =
      aiEngine === 'openai'
        ? dqAiSettings.openai_model
        : aiEngine === 'anthropic'
        ? normalizeAnthropicModel(dqAiSettings.anthropic_model)
        : aiEngine === 'gemini'
        ? dqAiSettings.gemini_model
        : '';
    const maxRequestTokens =
      aiEngine === 'openai'
        ? dqAiSettings.openai_max_request_tokens
        : aiEngine === 'anthropic'
        ? dqAiSettings.anthropic_max_request_tokens
        : aiEngine === 'gemini'
        ? dqAiSettings.gemini_max_request_tokens
        : undefined;
    const maxResponseTokens =
      aiEngine === 'openai'
        ? dqAiSettings.openai_max_response_tokens
        : aiEngine === 'anthropic'
        ? dqAiSettings.anthropic_max_response_tokens
        : aiEngine === 'gemini'
        ? dqAiSettings.gemini_max_response_tokens
        : undefined;
    const aiTemperature = Math.max(0, Math.min(2, Number(dqAiSettings.temperature ?? 0.1)));
    return { aiEngine, aiKey, aiModel, maxRequestTokens, maxResponseTokens, aiTemperature };
  };

  const getAiEngineConfigFor = useCallback((engine) => {
    const aiEngine = engine || dqAiSettings.default_engine || dqAiSettings.engine || 'openai';
    const aiKey =
      aiEngine === 'openai'
        ? dqAiSettings.openai_api_key
        : aiEngine === 'anthropic'
        ? dqAiSettings.anthropic_api_key
        : aiEngine === 'gemini'
        ? dqAiSettings.gemini_api_key
        : '';
    const aiModel =
      aiEngine === 'openai'
        ? dqAiSettings.openai_model
        : aiEngine === 'anthropic'
        ? normalizeAnthropicModel(dqAiSettings.anthropic_model)
        : aiEngine === 'gemini'
        ? dqAiSettings.gemini_model
        : '';
    const aiTemperature = Math.max(0, Math.min(2, Number(dqAiSettings.temperature ?? 0.1)));
    return { aiEngine, aiKey, aiModel, aiTemperature };
  }, [dqAiSettings, normalizeAnthropicModel]);

  const fetchDataQualityConfig = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/config/data-quality`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      const prompt = String(data?.config?.default_prompt || '').trim();
      if (prompt) {
        setDataQualityPrompt(prompt);
        try {
          localStorage.setItem(DEFAULT_PROMPT_STORAGE_KEY, prompt);
        } catch (err) {
          // ignore local storage
        }
      }
      const accountAiSettings = data?.config?.ai_settings;
      if (accountAiSettings && typeof accountAiSettings === 'object') {
        setDqAiSettings((prev) => ({ ...prev, ...DEFAULT_DQ_AI_SETTINGS, ...accountAiSettings }));
      }
      const accountFieldConfigs = data?.config?.field_configs;
      if (accountFieldConfigs && typeof accountFieldConfigs === 'object') {
        setFieldConfigs(accountFieldConfigs);
        try {
          localStorage.setItem(DQ_FIELD_CONFIGS_KEY, JSON.stringify(accountFieldConfigs));
        } catch (err) {
          // ignore local storage
        }
      }
    } catch (err) {
      // ignore config load errors
    }
  }, [getAuthHeaders, DEFAULT_PROMPT_STORAGE_KEY, DQ_FIELD_CONFIGS_KEY]);

  const fetchTaxonomyRoots = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/taxonomy/roots`, { headers });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) return;
      const items = Array.isArray(data?.items) ? data.items : [];
      setTaxonomyRootOptions(
        items.map((item) => ({
          value: item.id,
          label: item.path || item.name,
        }))
      );
    } catch (err) {
      // ignore taxonomy root load errors
    }
  }, [getAuthHeaders]);

  const saveDataQualityConfig = useCallback(async ({ promptValue, aiSettingsValue, fieldConfigsValue } = {}) => {
    const nextPrompt =
      typeof promptValue === 'string' ? promptValue : dataQualityPrompt;
    const nextAiSettings =
      aiSettingsValue && typeof aiSettingsValue === 'object'
        ? { ...DEFAULT_DQ_AI_SETTINGS, ...aiSettingsValue }
        : { ...DEFAULT_DQ_AI_SETTINGS, ...dqAiSettings };
    const nextFieldConfigs =
      fieldConfigsValue && typeof fieldConfigsValue === 'object'
        ? fieldConfigsValue
        : fieldConfigs;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/config/data-quality`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          default_prompt: nextPrompt,
          ai_settings: nextAiSettings,
          field_configs: nextFieldConfigs,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      throw err;
    }
  }, [dataQualityPrompt, dqAiSettings, fieldConfigs, getAuthHeaders]);

  const saveDataQualityPrompt = useCallback(async (promptValue) => {
    await saveDataQualityConfig({ promptValue });
  }, [saveDataQualityConfig]);

  const openStandardizeModal = useCallback(
    (codes) => {
      const normalized = Array.isArray(codes)
        ? codes.map((code) => String(code || '').trim()).filter(Boolean)
        : [];
      setStandardizeProductCodes(normalized);
      setStandardizeEngine(dqAiSettings.default_engine || dqAiSettings.engine || 'openai');
      setStandardizePrompt(dataQualityPrompt);
      setStandardizeTargetFields([]);
      setStandardizeDebugFirstRecord(false);
      setStandardizeExcludeOwnFieldValueSource(false);
      setStandardizeResults([]);
      setStandardizeModalVisible(true);
    },
    [dataQualityPrompt, dqAiSettings.default_engine, dqAiSettings.engine]
  );

  const parseJsonValue = useCallback((value, fallback = null) => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return fallback;
    try {
      return JSON.parse(value);
    } catch (err) {
      return fallback;
    }
  }, []);

  const getAiStandardizeStatus = useCallback((aiStandardized) => {
    const parsed = parseJsonValue(aiStandardized, null);
    if (!parsed || typeof parsed !== 'object') return { hasData: false, count: 0 };
    const keys = Object.keys(parsed || {});
    return { hasData: keys.length > 0, count: keys.length };
  }, [parseJsonValue]);

  const getFieldValueForReview = useCallback(
    (product, fieldKey, isStandardField, source = 'input') => {
      if (!product || !fieldKey) return '';
      const key = String(fieldKey || '').trim();
      const normalizedAttr = key.startsWith('attr_') ? key.slice(5) : key;
      const sourcePayload =
        source === 'ai'
          ? parseJsonValue(product.ai_standardized, {})
          : {
              name: product.name,
              description: product.description,
              category: product.category,
              manufacturer_name: product.manufacturer_name,
              manufacturer_part_number: product.manufacturer_part_number,
              attributes: parseJsonValue(product.attributes, product.attributes || {}),
            };

      if (isStandardField) {
        return sourcePayload?.[key] ?? '';
      }
      const attrs = parseJsonValue(sourcePayload?.attributes, sourcePayload?.attributes || {});
      return attrs?.[normalizedAttr] ?? '';
    },
    [parseJsonValue]
  );

  const parseProductCodes = useCallback((rawValue) => {
    if (!rawValue) return [];
    return String(rawValue)
      .split(/[\s,]+/)
      .map((code) => String(code || '').trim())
      .filter(Boolean);
  }, []);

  const updateSummaryCodes = useCallback(
    (rawValue) => {
      setSummaryStandardizeCodes(parseProductCodes(rawValue));
    },
    [parseProductCodes]
  );

  const updateStandardizeCodes = useCallback(
    (rawValue) => {
      setStandardizeProductCodes(parseProductCodes(rawValue));
    },
    [parseProductCodes]
  );

  const appendStandardizeCodes = useCallback(
    (codes) => {
      const incoming = Array.isArray(codes)
        ? codes.map((code) => String(code || '').trim()).filter(Boolean)
        : [];
      if (!incoming.length) return;
      setStandardizeProductCodes((prev) =>
        Array.from(new Set([...(prev || []), ...incoming]))
      );
    },
    []
  );

  const getMergedStandardizeCodes = useCallback(() => {
    const selectedCodes = (selectedIssueRows || [])
      .map((row) => String(row?.product_code || '').trim())
      .filter(Boolean);
    return Array.from(new Set([...(standardizeProductCodes || []), ...selectedCodes]));
  }, [selectedIssueRows, standardizeProductCodes]);

  const loadRecommendationProducts = useCallback(
    async (recKey, fieldKey, isStandardField, currentValues) => {
      const normalizedValues = Array.isArray(currentValues)
        ? currentValues.map((val) => String(val || '').trim()).filter(Boolean)
        : [];
      if (!normalizedValues.length) {
        setRecProductsByKey((prev) => ({
          ...prev,
          [recKey]: {
            loading: false,
            error: 'No values to lookup',
            products: [],
            open: true,
          },
        }));
        return;
      }

      setRecProductsByKey((prev) => ({
        ...prev,
        [recKey]: {
          ...(prev[recKey] || {}),
          loading: true,
          error: '',
          open: true,
        },
      }));

      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/recommendation-products`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            field_key: fieldKey,
            is_standard_field: Boolean(isStandardField),
            current_values: normalizedValues,
            limit: 25,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `HTTP ${resp.status}`);
        }
        setRecProductsByKey((prev) => ({
          ...prev,
          [recKey]: {
            loading: false,
            error: '',
            open: true,
            products: data?.products || [],
          },
        }));
      } catch (err) {
        setRecProductsByKey((prev) => ({
          ...prev,
          [recKey]: {
            loading: false,
            error: err.message || 'Unable to load products',
            open: true,
            products: [],
          },
        }));
      }
    },
    [getAuthHeaders]
  );

  const loadStandardizeDetail = useCallback(
    async (productCode) => {
      const code = String(productCode || '').trim();
      if (!code) return;
      setStandardizeDetailLoading(true);
      setStandardizeDetailError('');
      setStandardizeDetailProduct(null);
      try {
        const resp = await fetch(
          `${API_BASE_URL}/api/v1/products/by-code/${encodeURIComponent(code)}`,
          { headers: getAuthHeaders() }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `HTTP ${resp.status}`);
        }
        setStandardizeDetailProduct(data?.product || null);
      } catch (err) {
        setStandardizeDetailError(err.message || 'Unable to load product details');
      } finally {
        setStandardizeDetailLoading(false);
      }
    },
    [getAuthHeaders]
  );

  const submitStandardizeReviewDecision = useCallback(
    async ({ productCode, decision, fields = [], refreshRecommendationContext = null }) => {
      const code = String(productCode || '').trim();
      const normalizedDecision = String(decision || '').trim().toLowerCase();
      if (!code || !['approve', 'reject'].includes(normalizedDecision)) return;

      const actionKey = `${code}_${normalizedDecision}`;
      setReviewActionLoading((prev) => ({ ...prev, [actionKey]: true }));
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/ai-standardize/review-decision`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_code: code,
            decision: normalizedDecision,
            fields: Array.isArray(fields) ? fields : [],
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `HTTP ${resp.status}`);
        }

        message.success(
          normalizedDecision === 'approve'
            ? `Applied AI standardized values to ${code}`
            : `Rejected AI standardized values for ${code}`
        );

        if (refreshRecommendationContext) {
          const { recKey, fieldKey, isStandardField, currentValues } = refreshRecommendationContext;
          if (recKey && fieldKey && Array.isArray(currentValues) && currentValues.length) {
            await loadRecommendationProducts(recKey, fieldKey, isStandardField, currentValues);
          }
        }

        if (standardizeDetailProduct?.product_code === code) {
          await loadStandardizeDetail(code);
        }
      } catch (err) {
        message.error(err.message || 'Unable to process review decision');
      } finally {
        setReviewActionLoading((prev) => ({ ...prev, [actionKey]: false }));
      }
    },
    [getAuthHeaders, loadRecommendationProducts, loadStandardizeDetail, standardizeDetailProduct]
  );

  const submitBulkStandardizeReviewDecision = useCallback(
    async ({
      recKey,
      products = [],
      decision,
      fields = [],
      refreshRecommendationContext = null,
    }) => {
      const normalizedDecision = String(decision || '').trim().toLowerCase();
      if (!recKey || !['approve', 'reject'].includes(normalizedDecision)) return;
      const codes = (Array.isArray(products) ? products : [])
        .map((p) => String(p?.product_code || '').trim())
        .filter(Boolean);
      if (!codes.length) {
        message.info('No products loaded to apply bulk review action');
        return;
      }

      const bulkActionKey = `${recKey}_${normalizedDecision}`;
      setReviewBulkLoading((prev) => ({ ...prev, [bulkActionKey]: true }));

      let successCount = 0;
      let failCount = 0;
      try {
        for (const code of codes) {
          const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/ai-standardize/review-decision`, {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              product_code: code,
              decision: normalizedDecision,
              fields: Array.isArray(fields) ? fields : [],
            }),
          });
          if (resp.ok) {
            successCount += 1;
          } else {
            failCount += 1;
          }
        }

        if (successCount > 0) {
          message.success(
            `${normalizedDecision === 'approve' ? 'Approved' : 'Rejected'} ${successCount} product${
              successCount > 1 ? 's' : ''
            }`
          );
        }
        if (failCount > 0) {
          message.warning(`${failCount} product${failCount > 1 ? 's' : ''} failed to update`);
        }

        if (refreshRecommendationContext) {
          const { fieldKey, isStandardField, currentValues } = refreshRecommendationContext;
          if (fieldKey && Array.isArray(currentValues) && currentValues.length) {
            await loadRecommendationProducts(recKey, fieldKey, isStandardField, currentValues);
          }
        }

        if (standardizeDetailProduct?.product_code && codes.includes(standardizeDetailProduct.product_code)) {
          await loadStandardizeDetail(standardizeDetailProduct.product_code);
        }
      } catch (err) {
        message.error(err.message || 'Unable to process bulk review decision');
      } finally {
        setReviewBulkLoading((prev) => ({ ...prev, [bulkActionKey]: false }));
      }
    },
    [getAuthHeaders, loadRecommendationProducts, loadStandardizeDetail, standardizeDetailProduct]
  );

  const submitApplyChange = useCallback(
    async ({ productCode, field, oldValue, newValue, refreshCtx }) => {
      const code = String(productCode || '').trim();
      if (!code || !field) return;
      const actionKey = `${code}_approve`;
      setReviewActionLoading((prev) => ({ ...prev, [actionKey]: true }));
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/apply-changes`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            changes: [{ product_code: code, field, old_value: String(oldValue ?? ''), new_value: newValue }],
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.detail || `HTTP ${resp.status}`);
        message.success(`Updated ${code} — "${newValue}"`);
        if (refreshCtx) {
          const { recKey: rKey, fieldKey: fk, isStandardField: isStd, currentValues } = refreshCtx;
          if (rKey && fk && Array.isArray(currentValues) && currentValues.length) {
            await loadRecommendationProducts(rKey, fk, isStd, currentValues);
          }
        }
      } catch (err) {
        message.error(err.message || 'Failed to apply change');
      } finally {
        setReviewActionLoading((prev) => ({ ...prev, [actionKey]: false }));
      }
    },
    [getAuthHeaders, loadRecommendationProducts],
  );

  const applyBulkCustomValue = useCallback(
    async ({ recKey: rKey, fieldKey: fk, isStandardField: isStd, oldValues, newValue, refreshCtx }) => {
      const bulkActionKey = `${rKey}_approve`;
      setReviewBulkLoading((prev) => ({ ...prev, [bulkActionKey]: true }));
      try {
        const normalizedFieldName = isStd ? fk : (fk.startsWith('attr_') ? fk.slice(5) : fk);
        const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/bulk-standardize`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            field_name: normalizedFieldName,
            is_standard_field: isStd,
            old_values: oldValues,
            new_value: newValue,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.detail || `HTTP ${resp.status}`);
        message.success(`Applied "${newValue}" to ${data.updated_count ?? '?'} products`);
        if (refreshCtx) {
          const { fieldKey: rfk, isStandardField: risStd, currentValues } = refreshCtx;
          if (rfk && Array.isArray(currentValues) && currentValues.length) {
            await loadRecommendationProducts(rKey, rfk, risStd, currentValues);
          }
        }
      } catch (err) {
        message.error(err.message || 'Failed to apply bulk changes');
      } finally {
        setReviewBulkLoading((prev) => ({ ...prev, [bulkActionKey]: false }));
      }
    },
    [getAuthHeaders, loadRecommendationProducts],
  );

  const getUncategorizedCount = useCallback((parsed) => {
    if (!parsed) return 0;
    const goodCount = (parsed.good_values || []).length;
    const inSuggestions = parsed.values_covered_by_suggestions || 0;
    const totalUnique = parsed.total_unique_values || 0;
    return Math.max(0, totalUnique - goodCount - inSuggestions);
  }, []);

  const buildChangeRecommendations = (field, fieldKey, suggestions, idPrefix) => {
    const recs = [];
    (suggestions || []).forEach((s, i) => {
      const currentValues = Array.isArray(s.current_values) ? s.current_values : [];
      const correctedValues = Array.isArray(s.corrected_values) ? s.corrected_values : [];
      const reason = s.reason || '';
      const suggestedValue = s.suggested_value || correctedValues[0] || '';

      if (
        currentValues.length > 1 &&
        correctedValues.length === currentValues.length
      ) {
        currentValues.forEach((value, idx) => {
          const corrected = correctedValues[idx] || '';
          if (!value || !corrected) return;
          recs.push({
            id: `${fieldKey}_${idPrefix}_${i}_${idx}`,
            type: 'change',
            fieldKey,
            fieldName: field.name,
            fieldLabel: field.label || field.name,
            isStandardField: field.fieldType === 'standard',
            currentValues: [value],
            correctedValues: [corrected],
            suggestedValue: corrected,
            reason,
            affectedCount: currentValues.length ? Math.floor((s.affected_count || 0) / currentValues.length) : 0,
            existingCount: currentValues.length ? Math.floor((s.existing_count || 0) / currentValues.length) : 0,
            valuesCovered: 1,
          });
        });
      } else {
        recs.push({
          id: `${fieldKey}_${idPrefix}_${i}`,
          type: 'change',
          fieldKey,
          fieldName: field.name,
          fieldLabel: field.label || field.name,
          isStandardField: field.fieldType === 'standard',
          currentValues,
          correctedValues,
          suggestedValue,
          reason,
          affectedCount: s.affected_count || 0,
          existingCount: s.existing_count || 0,
          valuesCovered: s.values_covered || currentValues.length,
        });
      }
    });
    return recs;
  };

  const runAiStandardize = useCallback(async () => {
    const mergedCodes = getMergedStandardizeCodes();
    if (!mergedCodes.length) {
      message.warning('Add at least one product code');
      return;
    }
    setStandardizeProductCodes(mergedCodes);
    setStandardizeRunning(true);
    setStandardizeResults([]);
    setStandardizeConsistencyReport(null);
    try {
      const sanitizedFieldConfigs = Object.entries(fieldConfigs || {}).reduce((acc, [fieldKey, cfg]) => {
        const customPrompt = String(cfg?.custom_prompt || '').trim();
        const goldenValues = Array.isArray(cfg?.golden_values)
          ? cfg.golden_values.map((v) => String(v || '').trim()).filter(Boolean)
          : [];
        const additionalFields = Array.isArray(cfg?.additional_fields)
          ? cfg.additional_fields.map((v) => String(v || '').trim()).filter(Boolean)
          : [];
        if (!customPrompt && goldenValues.length === 0 && additionalFields.length === 0) return acc;
        acc[fieldKey] = {
          custom_prompt: customPrompt,
          golden_values: goldenValues,
          additional_fields: additionalFields,
        };
        return acc;
      }, {});
      const { aiEngine, aiKey, aiModel, aiTemperature } = getAiEngineConfigFor(standardizeEngine);
      const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/ai-standardize`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_codes: mergedCodes,
          ai_engine: aiEngine,
          ai_api_key: aiKey,
          ai_model: aiModel,
          parallel_workers: Math.max(1, Math.min(12, Number(dqAiSettings.parallel_workers || 1))),
          taxonomy_root_id: dqAiSettings.taxonomy_root_id,
          ai_temperature: aiTemperature,
          debug_first_record: standardizeDebugFirstRecord,
          exclude_own_field_value_source: standardizeExcludeOwnFieldValueSource,
          system_prompt: standardizePrompt,
          field_configs: sanitizedFieldConfigs,
          target_fields: standardizeTargetFields,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setStandardizeResults(data?.results || []);
      setStandardizeConsistencyReport(data?.consistency_report || null);
      message.success('AI standardization completed');
    } catch (err) {
      message.error(err.message || 'Unable to run AI standardization');
    } finally {
      setStandardizeRunning(false);
    }
  }, [
    getAiEngineConfigFor,
    getAuthHeaders,
    standardizeEngine,
    getMergedStandardizeCodes,
    standardizePrompt,
    fieldConfigs,
    standardizeTargetFields,
    dqAiSettings.parallel_workers,
    dqAiSettings.taxonomy_root_id,
    standardizeDebugFirstRecord,
    standardizeExcludeOwnFieldValueSource,
  ]);

  const runAiConsistencyDryRun = useCallback(async () => {
    const mergedCodes = getMergedStandardizeCodes();
    if (!mergedCodes.length) {
      message.warning('Add at least one product code');
      return;
    }
    setStandardizeProductCodes(mergedCodes);
    setStandardizeDryRunRunning(true);
    setStandardizeResults([]);
    setStandardizeConsistencyReport(null);
    try {
      const sanitizedFieldConfigs = Object.entries(fieldConfigs || {}).reduce((acc, [fieldKey, cfg]) => {
        const customPrompt = String(cfg?.custom_prompt || '').trim();
        const goldenValues = Array.isArray(cfg?.golden_values)
          ? cfg.golden_values.map((v) => String(v || '').trim()).filter(Boolean)
          : [];
        const additionalFields = Array.isArray(cfg?.additional_fields)
          ? cfg.additional_fields.map((v) => String(v || '').trim()).filter(Boolean)
          : [];
        if (!customPrompt && goldenValues.length === 0 && additionalFields.length === 0) return acc;
        acc[fieldKey] = {
          custom_prompt: customPrompt,
          golden_values: goldenValues,
          additional_fields: additionalFields,
        };
        return acc;
      }, {});
      const { aiEngine, aiKey, aiModel, aiTemperature } = getAiEngineConfigFor(standardizeEngine);
      const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/ai-standardize/consistency-dry-run`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_codes: mergedCodes,
          ai_engine: aiEngine,
          ai_api_key: aiKey,
          ai_model: aiModel,
          parallel_workers: Math.max(1, Math.min(12, Number(dqAiSettings.parallel_workers || 1))),
          taxonomy_root_id: dqAiSettings.taxonomy_root_id,
          ai_temperature: aiTemperature,
          debug_first_record: standardizeDebugFirstRecord,
          exclude_own_field_value_source: standardizeExcludeOwnFieldValueSource,
          system_prompt: standardizePrompt,
          field_configs: sanitizedFieldConfigs,
          target_fields: standardizeTargetFields,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setStandardizeResults(data?.results || []);
      setStandardizeConsistencyReport(data?.consistency_report || null);
      message.success('Consistency dry-run completed (no product updates applied)');
    } catch (err) {
      message.error(err.message || 'Unable to run consistency dry-run');
    } finally {
      setStandardizeDryRunRunning(false);
    }
  }, [
    getAiEngineConfigFor,
    getAuthHeaders,
    standardizeEngine,
    getMergedStandardizeCodes,
    standardizePrompt,
    fieldConfigs,
    standardizeTargetFields,
    dqAiSettings.parallel_workers,
    dqAiSettings.taxonomy_root_id,
    standardizeDebugFirstRecord,
    standardizeExcludeOwnFieldValueSource,
  ]);

  const visibleStandardizeResults = useMemo(() => {
    if (!standardizeNeedsReviewOnly) return standardizeResults || [];
    return (standardizeResults || []).filter((item) => {
      const status = String(item?.consistency?.category_consistency_status || '').trim().toLowerCase();
      return status === 'needs_review';
    });
  }, [standardizeNeedsReviewOnly, standardizeResults]);

  const parseAndApplyFieldResult = (field, fieldKey, resultText) => {
    if (!fieldKey || !resultText) return null;

    let parsed = null;
    const cleanedResult = resultText.replace(/```json|```/gi, '').trim();

    const tryParseJson = (str) => {
      try {
        const obj = JSON.parse(str);
        if (obj && typeof obj === 'object') {
          return obj;
        }
      } catch (e) {
        // ignore
      }
      return null;
    };

    const tryRepairAndParseJson = (str) => {
      let repaired = str.trim();
      let braceCount = 0;
      let bracketCount = 0;
      let inString = false;
      let escape = false;

      for (let i = 0; i < repaired.length; i++) {
        const char = repaired[i];
        if (escape) {
          escape = false;
          continue;
        }
        if (char === '\\') {
          escape = true;
          continue;
        }
        if (char === '"') {
          inString = !inString;
          continue;
        }
        if (inString) continue;

        if (char === '{') braceCount++;
        else if (char === '}') braceCount--;
        else if (char === '[') bracketCount++;
        else if (char === ']') bracketCount--;
      }

      if (inString) {
        repaired += '"';
      }
      while (bracketCount > 0) {
        repaired += ']';
        bracketCount--;
      }
      while (braceCount > 0) {
        repaired += '}';
        braceCount--;
      }

      return tryParseJson(repaired);
    };

    const extractAndParseJson = (text) => {
      let cleanText = text.replace(/---\s*Stream completed\s*---/gi, '').trim();
      cleanText = cleanText.replace(/```json|```/gi, '').trim();

      const codeBlockMatch = cleanText.match(/```(?:json)?\s*([\s\S]*?)(?:```|$)/);
      if (codeBlockMatch) {
        const jsonContent = codeBlockMatch[1].trim();
        if (jsonContent.startsWith('{')) {
          let obj = tryParseJson(jsonContent);
          if (obj) return obj;
          obj = tryRepairAndParseJson(jsonContent);
          if (obj) return obj;
        }
      }

      let braceCount = 0;
      let jsonStart = -1;
      let jsonEnd = -1;
      let inString = false;
      let escape = false;

      for (let i = 0; i < cleanText.length; i++) {
        const char = cleanText[i];
        if (escape) {
          escape = false;
          continue;
        }
        if (char === '\\') {
          escape = true;
          continue;
        }
        if (char === '"') {
          inString = !inString;
          continue;
        }
        if (inString) continue;

        if (char === '{') {
          if (braceCount === 0) jsonStart = i;
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          if (braceCount === 0 && jsonStart !== -1) {
            jsonEnd = i + 1;
            const jsonStr = cleanText.substring(jsonStart, jsonEnd);
            const obj = tryParseJson(jsonStr);
            if (obj) return obj;
            jsonStart = -1;
          }
        }
      }

      if (jsonStart !== -1 && braceCount > 0) {
        const truncatedJson = cleanText.substring(jsonStart);
        const obj = tryRepairAndParseJson(truncatedJson);
        if (obj) return obj;
      }

      const obj = tryParseJson(cleanText);
      if (obj) return obj;

      const repairedObj = tryRepairAndParseJson(cleanText);
      if (repairedObj) return repairedObj;

      return null;
    };

    parsed = extractAndParseJson(cleanedResult);
    if (!parsed) {
      parsed = tryParseJson(cleanedResult) || tryRepairAndParseJson(cleanedResult);
    }

    if (parsed) {
      const totalUnique = parsed.total_unique_values || parsed.totalUniqueValues || 0;

      let suggestions = (parsed.standardization_suggestions || parsed.standardizationSuggestions || [])
        .filter((s) => s && (s.current_values || s.currentValues))
        .map((s) => {
          const currentValues = Array.isArray(s.current_values || s.currentValues)
            ? (s.current_values || s.currentValues)
            : [];
          const correctedValues = Array.isArray(s.corrected_values || s.correctedValues)
            ? (s.corrected_values || s.correctedValues)
            : [];
          return {
            current_values: currentValues,
            corrected_values: correctedValues,
            suggested_value:
              s.suggested_value ||
              s.suggestedValue ||
              (correctedValues.length > 0 ? correctedValues[0] : ''),
            reason: s.reason || s.pattern || '',
            affected_count: s.affected_count || s.affectedCount || 0,
            existing_count: s.existing_count || s.existingCount || 0,
            values_covered: s.values_covered || s.valuesCovered || currentValues.length,
          };
        });

      const valuesInSuggestionsSet = new Set();
      suggestions.forEach((s) => {
        (s.current_values || []).forEach((v) => valuesInSuggestionsSet.add(v));
      });
      const valuesCoveredBySuggestions = valuesInSuggestionsSet.size;
      const uncategorizedCount = parsed.uncategorized_count || parsed.uncategorizedCount || 0;

      let valuesNeedingChange =
        parsed.values_needing_change || parsed.valuesNeedingChange || 0;
      if (valuesNeedingChange === 0 && suggestions.length > 0) {
        valuesNeedingChange = valuesCoveredBySuggestions + uncategorizedCount;
      }

      const valuesAlreadyGood =
        parsed.values_already_good ||
        parsed.valuesAlreadyGood ||
        Math.max(0, totalUnique - valuesNeedingChange);

      parsed = {
        field_name: parsed.field_name || parsed.fieldName || field?.name || fieldKey,
        total_unique_values: totalUnique,
        values_needing_change: valuesNeedingChange,
        values_already_good: valuesAlreadyGood,
        values_covered_by_suggestions: valuesCoveredBySuggestions,
        uncategorized_count: uncategorizedCount,
        good_values: parsed.good_values || parsed.goodValues || [],
        standardization_suggestions: suggestions,
        recommended_canonical_values:
          parsed.recommended_canonical_values || parsed.recommendedCanonicalValues || [],
        summary: parsed.summary || '',
      };

      parsed.good_values = (parsed.good_values || [])
        .filter((v) => v && (v.value || v.name))
        .map((v) => ({
          value: v.value || v.name || '',
          count: v.count || v.product_count || 0,
        }));

      parsed.standardization_suggestions = (parsed.standardization_suggestions || [])
        .filter((s) => s && (s.current_values || s.currentValues))
        .map((s) => {
          const currentVals = Array.isArray(s.current_values || s.currentValues)
            ? (s.current_values || s.currentValues)
            : [];
          const correctedVals = Array.isArray(s.corrected_values || s.correctedValues)
            ? (s.corrected_values || s.correctedValues)
            : [];
          return {
            current_values: currentVals,
            corrected_values: correctedVals,
            suggested_value:
              s.suggested_value ||
              s.suggestedValue ||
              (correctedVals.length > 0 ? correctedVals[0] : ''),
            reason: s.reason || '',
            affected_count: s.affected_count || s.affectedCount || 0,
            existing_count: s.existing_count || s.existingCount || 0,
            values_covered: s.values_covered || s.valuesCovered || currentVals.length,
          };
        });
    }

    setAnalysisResults((prev) => ({
      ...prev,
      [fieldKey]: { result: resultText, parsed, loading: false },
    }));

    const newRecs = [];
    if (
      parsed &&
      Array.isArray(parsed.standardization_suggestions) &&
      parsed.standardization_suggestions.length > 0
    ) {
      newRecs.push(
        ...buildChangeRecommendations(field, fieldKey, parsed.standardization_suggestions, 'job')
      );
    }

    if (parsed && Array.isArray(parsed.good_values)) {
      parsed.good_values.forEach((g, i) => {
        if (!g.value) return;
        newRecs.push({
          id: `${fieldKey}_good_job_${i}`,
          type: 'no_change',
          fieldKey,
          fieldName: field?.name || fieldKey,
          fieldLabel: field?.label || field?.name || fieldKey,
          isStandardField: field?.fieldType === 'standard',
          currentValues: [g.value],
          correctedValues: [g.value],
          suggestedValue: g.value,
          reason: `No standardization needed for '${g.value}'`,
          affectedCount: 0,
          existingCount: g.count || 0,
          valuesCovered: 1,
        });
      });
    }

    if (newRecs.length > 0) {
      setRecommendations((prev) => [
        ...prev.filter((r) => r.fieldKey !== fieldKey),
        ...newRecs,
      ]);
    }

    if (parsed) {
      const goodCount = parsed?.good_values?.length || 0;
      const inSuggestions = parsed?.values_covered_by_suggestions || 0;
      const totalUnique = parsed?.total_unique_values || 0;
      const uncategorized = Math.max(0, totalUnique - goodCount - inSuggestions);
      if (uncategorized > 0 && !batchAnalysisState[fieldKey]?.running) {
        setTimeout(() => runFullBatchAnalysis(fieldKey), 0);
      }
    }

    return parsed;
  };

  
  // Products with issues
  const [productsWithIssues, setProductsWithIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issueTypeFilter, setIssueTypeFilter] = useState('all');
  const [issuesSearch, setIssuesSearch] = useState('');
  const [issuesPage, setIssuesPage] = useState(1);
  const [issuesPageSize, setIssuesPageSize] = useState(20);
  const [issuesTotalCount, setIssuesTotalCount] = useState(0);

  // Ambiguous alt part number / supplier part number mappings (one value
  // mapped to more than one distinct product_code).
  const [ambiguousMode, setAmbiguousMode] = useState('alt'); // 'alt' | 'supplier'
  const [ambiguousItems, setAmbiguousItems] = useState([]);
  const [ambiguousLoading, setAmbiguousLoading] = useState(false);
  const [ambiguousSearch, setAmbiguousSearch] = useState('');
  const [ambiguousPage, setAmbiguousPage] = useState(1);
  const [ambiguousPageSize, setAmbiguousPageSize] = useState(20);
  const [ambiguousTotalCount, setAmbiguousTotalCount] = useState(0);
  const [ambiguousExporting, setAmbiguousExporting] = useState(false);

  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) return;

    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/summary`, {
        method: 'GET',
        headers,
      });
      if (resp.ok) {
        const data = await resp.json();
        setSummary(data);
      } else {
        const error = await resp.json();
        message.error(error.detail || 'Failed to load data quality summary');
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
      message.error('Failed to load data quality summary');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch all analyzable fields (standard + attributes)
  const fetchFields = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) return;

    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/fields`, {
        method: 'GET',
        headers,
      });
      if (resp.ok) {
        const data = await resp.json();
        setAllFields(data);
      }
    } catch (err) {
      console.error('Error fetching fields:', err);
    }
  }, [getAuthHeaders]);

  // Fetch products with issues
  const fetchProductsWithIssues = useCallback(async ({
    issueType = issueTypeFilter,
    search = issuesSearch,
    page = issuesPage,
    pageSize = issuesPageSize,
  } = {}) => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) return;

    setIssuesLoading(true);
    try {
      const safePage = Math.max(1, Number(page) || 1);
      const safePageSize = Math.max(1, Number(pageSize) || 20);
      const offset = (safePage - 1) * safePageSize;
      const params = new URLSearchParams({
        issue_type: issueType,
        limit: String(safePageSize),
        offset: String(offset),
      });
      if (String(search || '').trim()) {
        params.set('search', String(search || '').trim());
      }
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/data-quality/products-with-issues?${params.toString()}`,
        { method: 'GET', headers }
      );
      if (resp.ok) {
        const data = await resp.json();
        setProductsWithIssues(data.products || []);
        setIssuesTotalCount(Number(data?.pagination?.total_count || 0));
      }
    } catch (err) {
      console.error('Error fetching products with issues:', err);
    } finally {
      setIssuesLoading(false);
    }
  }, [getAuthHeaders, issueTypeFilter, issuesSearch, issuesPage, issuesPageSize]);

  const ambiguousEndpointFor = useCallback(
    (mode) => (mode === 'supplier' ? 'ambiguous-supplier-part-numbers' : 'ambiguous-alt-part-numbers'),
    []
  );

  // Fetch alt part numbers / supplier part numbers mapped to multiple products
  const fetchAmbiguousItems = useCallback(async ({
    mode = ambiguousMode,
    search = ambiguousSearch,
    page = ambiguousPage,
    pageSize = ambiguousPageSize,
  } = {}) => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) return;

    setAmbiguousLoading(true);
    try {
      const safePage = Math.max(1, Number(page) || 1);
      const safePageSize = Math.max(1, Number(pageSize) || 20);
      const offset = (safePage - 1) * safePageSize;
      const params = new URLSearchParams({
        limit: String(safePageSize),
        offset: String(offset),
      });
      if (String(search || '').trim()) {
        params.set('search', String(search || '').trim());
      }
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/data-quality/${ambiguousEndpointFor(mode)}?${params.toString()}`,
        { method: 'GET', headers }
      );
      if (resp.ok) {
        const data = await resp.json();
        setAmbiguousItems(data.items || []);
        setAmbiguousTotalCount(Number(data?.pagination?.total_count || 0));
      } else {
        const error = await resp.json().catch(() => ({}));
        message.error(error.detail || 'Failed to load ambiguous mappings');
      }
    } catch (err) {
      console.error('Error fetching ambiguous mappings:', err);
      message.error('Failed to load ambiguous mappings');
    } finally {
      setAmbiguousLoading(false);
    }
  }, [getAuthHeaders, ambiguousEndpointFor, ambiguousMode, ambiguousSearch, ambiguousPage, ambiguousPageSize]);

  // Export the current mode's ambiguous mappings (honoring the active search
  // filter, but ignoring on-screen pagination -- export is always the full
  // matching set) as an Excel or CSV download.
  const exportAmbiguousItems = useCallback(async (format) => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) return;

    setAmbiguousExporting(true);
    try {
      const params = new URLSearchParams({ format });
      if (String(ambiguousSearch || '').trim()) {
        params.set('search', String(ambiguousSearch || '').trim());
      }
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/data-quality/${ambiguousEndpointFor(ambiguousMode)}?${params.toString()}`,
        { method: 'GET', headers }
      );
      if (!resp.ok) {
        const error = await resp.json().catch(() => ({}));
        message.error(error.detail || 'Export failed');
        return;
      }
      const blob = await resp.blob();
      const disposition = resp.headers.get('content-disposition') || '';
      const match = disposition.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `${ambiguousEndpointFor(ambiguousMode)}.${format}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting ambiguous mappings:', err);
      message.error('Export failed');
    } finally {
      setAmbiguousExporting(false);
    }
  }, [getAuthHeaders, ambiguousEndpointFor, ambiguousMode, ambiguousSearch]);

  const ambiguousColumns = useMemo(() => {
    const productCodesColumn = {
      title: 'Product Codes',
      dataIndex: 'product_codes',
      render: (codes) => {
        const list = codes || [];
        const shown = list.slice(0, 5);
        return (
          <span>
            {shown.map((code) => (
              <Tag key={code}>{code}</Tag>
            ))}
            {list.length > shown.length && <Tag>+{list.length - shown.length} more</Tag>}
          </span>
        );
      },
    };
    const itemCountColumn = {
      title: '# Items',
      dataIndex: 'item_count',
      width: 100,
      sorter: (a, b) => (a.item_count || 0) - (b.item_count || 0),
    };
    if (ambiguousMode === 'supplier') {
      return [
        { title: 'Supplier Name', dataIndex: 'supplier_name' },
        { title: 'Supplier Part Number', dataIndex: 'supplier_part_number' },
        itemCountColumn,
        productCodesColumn,
      ];
    }
    return [
      { title: 'Alt Part Number', dataIndex: 'alt_part_number' },
      itemCountColumn,
      productCodesColumn,
    ];
  }, [ambiguousMode]);

  // Combine and filter fields
  const combinedFields = useMemo(() => {
    const fields = [];
    
    // Add standard fields
    if (showStandardFields) {
      Object.entries(allFields.standard_fields || {}).forEach(([key, stats]) => {
        fields.push({
          key,
          ...stats,
          fieldType: 'standard',
        });
      });
    }
    
    // Add attribute fields
    if (showAttributeFields) {
      Object.entries(allFields.attribute_fields || {}).forEach(([key, stats]) => {
        fields.push({
          key,
          ...stats,
          fieldType: 'attribute',
        });
      });
    }
    
    // Filter by search
    if (!fieldSearch.trim()) return fields;
    const search = fieldSearch.toLowerCase();
    return fields.filter(
      (f) =>
        f.name?.toLowerCase().includes(search) ||
        f.label?.toLowerCase().includes(search)
    );
  }, [allFields, fieldSearch, showStandardFields, showAttributeFields]);

  const processAnalysisJobFields = useCallback(
    (jobFields) => {
      Object.entries(jobFields || {}).forEach(([fieldKey, fieldInfo]) => {
        if (analysisJobProcessedRef.current[fieldKey]) return;

        if (fieldInfo?.status === 'completed' && fieldInfo?.result) {
          const field =
            combinedFields.find((f) => f.key === fieldKey) || {
              key: fieldKey,
              name: fieldInfo.field_name || fieldKey,
              label: fieldInfo.field_name || fieldKey,
              fieldType: fieldInfo.is_standard_field ? 'standard' : 'attribute',
            };
          parseAndApplyFieldResult(field, fieldKey, fieldInfo.result);
          analysisJobProcessedRef.current[fieldKey] = true;
        }

        if (fieldInfo?.status === 'failed') {
          setAnalysisResults((prev) => ({
            ...prev,
            [fieldKey]: {
              result: fieldInfo.error || 'Analysis failed',
              parsed: null,
              loading: false,
            },
          }));
          analysisJobProcessedRef.current[fieldKey] = true;
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [combinedFields]
  );

  // Full field catalog without search filtering (for analysis consistency)
  const allFieldsCatalog = useMemo(() => {
    const fields = [];

    Object.entries(allFields.standard_fields || {}).forEach(([key, stats]) => {
      fields.push({
        key,
        ...stats,
        fieldType: 'standard',
      });
    });

    Object.entries(allFields.attribute_fields || {}).forEach(([key, stats]) => {
      fields.push({
        key,
        ...stats,
        fieldType: 'attribute',
      });
    });

    return fields;
  }, [allFields]);

  const additionalFieldOptions = useMemo(
    () =>
      allFieldsCatalog.map((f) => ({
        label: `${f.label || f.name} (${f.fieldType === 'standard' ? 'Standard' : 'Attribute'})`,
        value: f.key,
      })),
    [allFieldsCatalog]
  );

  const fieldLabelByKey = useMemo(() => {
    const map = {};
    allFieldsCatalog.forEach((f) => {
      map[f.key] = f.label || f.name || f.key;
    });
    return map;
  }, [allFieldsCatalog]);
  const watchedAdditionalFields = Form.useWatch('additional_fields', configForm) || [];

  const batchJobs = useMemo(() => {
    return Object.entries(batchAnalysisState || {}).map(([key, state]) => {
      const field = combinedFields.find((f) => f.key === key);
      const status = state.cancelled
        ? 'Cancelled'
        : state.running
        ? 'Running'
        : state.currentBatch
        ? 'Completed'
        : 'Idle';
      const progress =
        state.totalBatches && state.totalBatches > 0
          ? Math.min(100, Math.round(((state.currentBatch || 0) / state.totalBatches) * 100))
          : 0;
      return {
        key,
        fieldLabel: field?.label || field?.name || key,
        status,
        running: !!state.running,
        cancelled: !!state.cancelled,
        progress,
        currentBatch: state.currentBatch || 0,
        totalBatches: state.totalBatches || 0,
        remaining: state.remaining,
      };
    });
  }, [batchAnalysisState, combinedFields]);

  const fieldsWithUncategorized = useMemo(() => {
    return Object.entries(analysisResults || {})
      .map(([fieldKey, result]) => {
        const uncategorizedCount = getUncategorizedCount(result?.parsed);
        return { fieldKey, uncategorizedCount };
      })
      .filter((item) => item.uncategorizedCount > 0);
  }, [analysisResults, getUncategorizedCount]);

  const totalUncategorizedValues = useMemo(() => {
    return fieldsWithUncategorized.reduce((sum, item) => sum + item.uncategorizedCount, 0);
  }, [fieldsWithUncategorized]);

  const analysisInsights = useMemo(() => {
    const totalFields = allFieldsCatalog.length;
    const analyzedFields = new Set(
      Object.entries(analysisResults || {})
        .filter(([, result]) => result?.parsed)
        .map(([fieldKey]) => fieldKey)
    );
    const analyzedCount = analyzedFields.size;
    const pendingCount = Math.max(0, totalFields - analyzedCount);

    const fieldsWithAnyRecommendations = new Set(
      (recommendations || []).map((rec) => rec.fieldKey)
    );
    const fieldsNeedingAction = new Set(
      (recommendations || [])
        .filter((rec) => rec.type !== 'no_change')
        .map((rec) => rec.fieldKey)
    );

    const fieldsWithInstructions = new Set(
      Object.entries(fieldConfigs || {})
        .filter(
          ([, cfg]) =>
            (cfg?.custom_prompt || '').trim() || (cfg?.additional_fields || []).length > 0
        )
        .map(([fieldKey]) => fieldKey)
    );

    return {
      totalFields,
      analyzedCount,
      pendingCount,
      needsReviewCount: fieldsWithAnyRecommendations.size,
      needsActionCount: fieldsNeedingAction.size,
      uncategorizedFieldsCount: fieldsWithUncategorized.length,
      totalUncategorizedValues,
      fieldsWithInstructionsCount: fieldsWithInstructions.size,
      analyzedPercent: totalFields ? Math.round((analyzedCount / totalFields) * 100) : 0,
      actionPercent: analyzedCount
        ? Math.round((fieldsNeedingAction.size / analyzedCount) * 100)
        : 0,
    };
  }, [
    allFieldsCatalog,
    analysisResults,
    recommendations,
    fieldsWithUncategorized,
    totalUncategorizedValues,
    fieldConfigs,
  ]);

  // Select all visible fields
  const selectAllFields = () => {
    setSelectedFields(combinedFields.map((f) => f.key));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedFields([]);
  };

  // Persist default prompt
  useEffect(() => {
    try {
      localStorage.setItem(DEFAULT_PROMPT_STORAGE_KEY, dataQualityPrompt || '');
    } catch (err) {
      // ignore storage failures
    }
  }, [dataQualityPrompt, DEFAULT_PROMPT_STORAGE_KEY]);

  // Persist AI settings
  useEffect(() => {
    try {
      localStorage.setItem(DQ_AI_SETTINGS_KEY, JSON.stringify(dqAiSettings));
    } catch (err) {
      // ignore storage failures
    }
  }, [dqAiSettings, DQ_AI_SETTINGS_KEY]);

  useEffect(() => {
    try {
      localStorage.setItem(DQ_RECOMMENDATIONS_KEY, JSON.stringify(recommendations));
    } catch (err) {
      // ignore storage failures
    }
  }, [recommendations, DQ_RECOMMENDATIONS_KEY]);

  useEffect(() => {
    try {
      localStorage.setItem(DQ_FIELD_CONFIGS_KEY, JSON.stringify(fieldConfigs));
    } catch (err) {
      // ignore storage failures
    }
  }, [fieldConfigs, DQ_FIELD_CONFIGS_KEY]);

  useEffect(() => {
    if (!aiSettingsModalVisible) return;
    const loadAnthropicModels = async () => {
      setAnthropicModelsLoading(true);
      try {
        const headers = getAuthHeaders();
        if (!headers) return;
        const resp = await fetch(`${API_BASE_URL}/api/v1/config/ai/models/claude`, { headers });
        if (resp.ok) {
          const data = await resp.json();
          if (data?.available && Array.isArray(data.models)) {
            setAnthropicModels(data.models);
          }
        }
      } catch (err) {
        message.warning('Unable to load Anthropic models');
      } finally {
        setAnthropicModelsLoading(false);
      }
    };
    loadAnthropicModels();
  }, [aiSettingsModalVisible, getAuthHeaders]);

  const getEngineTokenDefaults = (engine, model) => {
    const name = (model || '').toLowerCase();
    if (engine === 'anthropic') {
      return {
        maxRequest: name.includes('haiku') || name.includes('sonnet') || name.includes('opus') ? 200000 : 100000,
        maxResponse: 4000,
      };
    }
    if (engine === 'openai') {
      if (name.includes('gpt-4o') || name.includes('gpt-4.1')) {
        return { maxRequest: 128000, maxResponse: 4000 };
      }
      if (name.includes('gpt-4-turbo')) {
        return { maxRequest: 128000, maxResponse: 4000 };
      }
      if (name.includes('gpt-4')) {
        return { maxRequest: 6000, maxResponse: 2000 };
      }
      if (name.includes('gpt-3.5')) {
        return { maxRequest: 12000, maxResponse: 3000 };
      }
      return { maxRequest: 32000, maxResponse: 4000 };
    }
    if (engine === 'gemini') {
      if (name.includes('1.5')) {
        return { maxRequest: 128000, maxResponse: 4096 };
      }
      return { maxRequest: 32768, maxResponse: 2048 };
    }
    return { maxRequest: 32000, maxResponse: 2000 };
  };

  const applyTokenDefaults = (engine, model) => {
    const defaults = getEngineTokenDefaults(engine, model);
    setDqAiSettings((prev) => {
      if (engine === 'openai') {
        return {
          ...prev,
          openai_max_request_tokens: defaults.maxRequest,
          openai_max_response_tokens: defaults.maxResponse,
        };
      }
      if (engine === 'anthropic') {
        return {
          ...prev,
          anthropic_max_request_tokens: defaults.maxRequest,
          anthropic_max_response_tokens: defaults.maxResponse,
        };
      }
      if (engine === 'gemini') {
        return {
          ...prev,
          gemini_max_request_tokens: defaults.maxRequest,
          gemini_max_response_tokens: defaults.maxResponse,
        };
      }
      return prev;
    });
  };

  const testAiEngine = async () => {
    const headers = getAuthHeaders();
    if (!headers || (!headers['X-API-Key'] && !headers['Authorization'])) {
      message.error('Authentication required. Please log in again.');
      return;
    }

    const engine = dqAiSettings.default_engine || dqAiSettings.engine || 'openai';
    const apiKey =
      engine === 'openai'
        ? dqAiSettings.openai_api_key
        : engine === 'anthropic'
        ? dqAiSettings.anthropic_api_key
        : engine === 'gemini'
        ? dqAiSettings.gemini_api_key
        : '';
    let model =
      engine === 'openai'
        ? dqAiSettings.openai_model
        : engine === 'anthropic'
        ? dqAiSettings.anthropic_model
        : engine === 'gemini'
        ? dqAiSettings.gemini_model
        : '';
    if (engine === 'anthropic') {
      const normalized = normalizeAnthropicModel(model);
      if (normalized !== model) {
        model = normalized;
        setDqAiSettings((prev) => ({ ...prev, anthropic_model: normalized }));
        message.warning(`Switched to supported Anthropic model: ${normalized}`);
      }
    }

    if (!apiKey || !model) {
      message.warning('Please provide both API key and model to test.');
      return;
    }

    setTestingAiEngine(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/config/ai/test`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          engine_type: engine,
          api_key: apiKey,
          model_name: model,
        }),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        message.error(errorData.detail || `Test failed (${resp.status})`);
        return;
      }

      const data = await resp.json();
      const result = data.test_result || {};
      if (data.success || result.status === 'healthy' || result.connection_test) {
        if (
          engine === 'anthropic' &&
          data.model &&
          String(data.model).trim() &&
          String(data.model).trim() !== String(dqAiSettings.anthropic_model || '').trim()
        ) {
          setDqAiSettings((prev) => ({ ...prev, anthropic_model: String(data.model).trim() }));
          message.warning(`Anthropic model auto-updated to ${data.model}`);
        }
        message.success(`${engine} connection test successful.`);
      } else {
        message.error(data.error || result.message || data.message || 'Connection test failed.');
      }
    } catch (err) {
      message.error(`Connection test failed: ${err.message}`);
    } finally {
      setTestingAiEngine(false);
    }
  };

  // Toggle field selection
  const toggleField = (key) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const clearFieldAcrossAllRecords = useCallback(
    async (field, e) => {
      e?.stopPropagation?.();
      if (!field?.key) return;
      const fieldKey = String(field.key || '').trim();
      const fieldLabel = String(field.label || field.name || fieldKey).trim();
      const hasValues = Math.max(0, Number(field.total_count || 0) - Number(field.empty_count || 0));
      Modal.confirm({
        title: 'Clear field across all products?',
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <div><strong>Field:</strong> {fieldLabel}</div>
            <div><strong>Type:</strong> {field.fieldType === 'standard' ? 'Standard' : 'Attribute'}</div>
            <div><strong>Estimated records affected:</strong> {hasValues}</div>
            <div style={{ marginTop: 8 }}>
              This will clear the field value from all records in this account.
            </div>
          </div>
        ),
        okText: 'Yes, clear field',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            setClearFieldLoadingMap((prev) => ({ ...prev, [fieldKey]: true }));
            const headers = getAuthHeaders();
            if (!headers || (!headers['X-API-Key'] && !headers['Authorization'])) {
              message.error('Authentication required. Please log in again.');
              return;
            }
            const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/clear-field`, {
              method: 'POST',
              headers: {
                ...headers,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                field_key: fieldKey,
                confirm: true,
              }),
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
              throw new Error(data?.detail || `HTTP ${resp.status}`);
            }
            const updated = Number(data?.updated_count || 0);
            message.success(`Cleared ${updated} record(s) for ${fieldLabel}`);
            await Promise.all([
              fetchSummary(),
              fetchFields(),
              fetchProductsWithIssues({
                issueType: issueTypeFilter,
                search: issuesSearch,
                page: issuesPage,
                pageSize: issuesPageSize,
              }),
            ]);
          } catch (err) {
            message.error(err.message || 'Unable to clear field across all records');
          } finally {
            setClearFieldLoadingMap((prev) => ({ ...prev, [fieldKey]: false }));
          }
        },
      });
    },
    [
      fetchFields,
      fetchProductsWithIssues,
      fetchSummary,
      getAuthHeaders,
      issueTypeFilter,
      issuesPage,
      issuesPageSize,
      issuesSearch,
    ]
  );

  // Open config drawer for a field
  const openFieldConfig = (field, e) => {
    e?.stopPropagation();
    setConfigField(field);
    setConfigDrawerVisible(true);
  };

  useEffect(() => {
    if (!configDrawerVisible || !configField) return;
    configForm.setFieldsValue({
      custom_prompt: fieldConfigs[configField.key]?.custom_prompt || '',
      golden_values: (fieldConfigs[configField.key]?.golden_values || []).join('\n'),
      additional_fields: (fieldConfigs[configField.key]?.additional_fields || []).filter(
        (key) => key !== configField.key
      ),
    });
  }, [configDrawerVisible, configField, configForm, fieldConfigs]);

  // Save field configuration
  const saveFieldConfig = async (values) => {
    if (!configField) return;
    const selectedAdditionalFields = Array.isArray(values.additional_fields)
      ? values.additional_fields
      : [];
    const nextFieldConfigs = {
      ...fieldConfigs,
      [configField.key]: {
        custom_prompt: values.custom_prompt || '',
        golden_values: (values.golden_values || '')
          .split('\n')
          .map((v) => v.trim())
          .filter((v) => v),
        additional_fields: selectedAdditionalFields.filter((key) => key !== configField.key),
      },
    };
    setFieldConfigs(nextFieldConfigs);
    try {
      await saveDataQualityConfig({ fieldConfigsValue: nextFieldConfigs });
      message.success(`Configuration saved for ${configField.label || configField.name}`);
      setConfigDrawerVisible(false);
    } catch (err) {
      message.error(err.message || 'Unable to save field configuration');
    }
  };

  // Analyze single field
  const analyzeField = async (field) => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) return;

    const fieldKey = field?.key || field?.name || field?.label || '';
    const config = fieldConfigs[fieldKey] || {};
    const effectivePrompt = config.custom_prompt || dataQualityPrompt || '';
    const { aiEngine, aiKey, aiModel, maxRequestTokens, maxResponseTokens, aiTemperature } = getAiEngineConfig();

    setAnalysisResults((prev) => ({
      ...prev,
      [fieldKey]: { loading: true, result: '', parsed: null },
    }));

    try {
    const fieldName =
      field?.name ||
      field?.key ||
      field?.label ||
      (typeof fieldKey === 'string' && fieldKey) ||
      '';
    if (!fieldName) {
      message.error('Selected field is missing a name. Please reselect and try again.');
      console.warn('DataQuality analyzeField missing field name:', { field, fieldKey });
      setAnalysisStep(0);
      return;
    }
      const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/ai-analyze-field`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          field_name: fieldName,
          is_standard_field: field?.fieldType === 'standard',
          custom_prompt: effectivePrompt,
          golden_values: config.golden_values || [],
          additional_fields: config.additional_fields || [],
          ai_engine: aiEngine,
          ai_api_key: aiKey,
          ai_model: aiModel,
          max_request_tokens: maxRequestTokens,
          max_response_tokens: maxResponseTokens,
          taxonomy_root_id: dqAiSettings.taxonomy_root_id,
          ai_temperature: aiTemperature,
        }),
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText || 'Field analysis failed');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let result = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        result += chunk;
        const nextResult = result;
        setAnalysisResults((prev) => ({
          ...prev,
          [fieldKey]: { ...prev[fieldKey], result: nextResult, loading: true },
        }));
      }

      // Try to parse as JSON - handle various formats including truncated responses
      let parsed = null;
      const cleanedResult = result.replace(/```json|```/gi, '').trim();
      
      const tryParseJson = (str) => {
        try {
          const obj = JSON.parse(str);
          // Validate it has expected structure
          if (obj && typeof obj === 'object') {
            return obj;
          }
        } catch (e) {
          // Not valid JSON
        }
        return null;
      };
      
      // Try to repair truncated JSON by closing open brackets/braces
      const tryRepairAndParseJson = (str) => {
        let repaired = str.trim();
        
        // Remove trailing incomplete content after last complete object/array
        // Count brackets and braces
        let braceCount = 0;
        let bracketCount = 0;
        let inString = false;
        let escape = false;
        
        for (let i = 0; i < repaired.length; i++) {
          const char = repaired[i];
          if (escape) {
            escape = false;
            continue;
          }
          if (char === '\\') {
            escape = true;
            continue;
          }
          if (char === '"') {
            inString = !inString;
            continue;
          }
          if (inString) continue;
          
          if (char === '{') braceCount++;
          else if (char === '}') braceCount--;
          else if (char === '[') bracketCount++;
          else if (char === ']') bracketCount--;
        }
        
        // If we're in a string, try to close it
        if (inString) {
          repaired += '"';
        }
        
        // Close any open brackets/braces
        while (bracketCount > 0) {
          repaired += ']';
          bracketCount--;
        }
        while (braceCount > 0) {
          repaired += '}';
          braceCount--;
        }
        
        return tryParseJson(repaired);
      };
      
      const extractAndParseJson = (text) => {
        // Clean up the text - remove stream markers
        let cleanText = text.replace(/---\s*Stream completed\s*---/gi, '').trim();
        cleanText = cleanText.replace(/```json|```/gi, '').trim();
        
        // Method 1: Try to extract JSON from markdown code blocks (handle unclosed blocks too)
        const codeBlockMatch = cleanText.match(/```(?:json)?\s*([\s\S]*?)(?:```|$)/);
        if (codeBlockMatch) {
          const jsonContent = codeBlockMatch[1].trim();
          if (jsonContent.startsWith('{')) {
            // Try direct parse first
            let obj = tryParseJson(jsonContent);
            if (obj) return obj;
            // Try to repair truncated JSON
            obj = tryRepairAndParseJson(jsonContent);
            if (obj) return obj;
          }
        }
        
        // Method 2: Find the outermost JSON object by matching balanced braces
        let braceCount = 0;
        let jsonStart = -1;
        let jsonEnd = -1;
        let inString = false;
        let escape = false;
        
        for (let i = 0; i < cleanText.length; i++) {
          const char = cleanText[i];
          if (escape) {
            escape = false;
            continue;
          }
          if (char === '\\') {
            escape = true;
            continue;
          }
          if (char === '"') {
            inString = !inString;
            continue;
          }
          if (inString) continue;
          
          if (char === '{') {
            if (braceCount === 0) jsonStart = i;
            braceCount++;
          } else if (char === '}') {
            braceCount--;
            if (braceCount === 0 && jsonStart !== -1) {
              jsonEnd = i + 1;
              // Try to parse this JSON block
              const jsonStr = cleanText.substring(jsonStart, jsonEnd);
              const obj = tryParseJson(jsonStr);
              if (obj) return obj;
              // If parse failed, continue looking for another JSON block
              jsonStart = -1;
            }
          }
        }
        
        // Method 3: If we found a start but JSON is truncated, try to repair it
        if (jsonStart !== -1 && braceCount > 0) {
          const truncatedJson = cleanText.substring(jsonStart);
          const obj = tryRepairAndParseJson(truncatedJson);
          if (obj) return obj;
        }
        
        // Method 4: Try the entire text as JSON (in case it's pure JSON)
        const obj = tryParseJson(cleanText);
        if (obj) return obj;
        
        // Method 5: Try to repair the entire text
        const repairedObj = tryRepairAndParseJson(cleanText);
        if (repairedObj) return repairedObj;
        
        return null;
      };
      
      parsed = extractAndParseJson(cleanedResult);
      if (!parsed) {
        parsed = tryParseJson(cleanedResult) || tryRepairAndParseJson(cleanedResult);
      }
      
      // Normalize the parsed object to ensure consistent structure
      if (parsed) {
        const totalUnique = parsed.total_unique_values || parsed.totalUniqueValues || 0;
        
        // Normalize suggestions first to calculate covered values
        // Handle both old format (suggested_value) and new format (corrected_values array)
        let suggestions = (parsed.standardization_suggestions || parsed.standardizationSuggestions || [])
          .filter(s => s && (s.current_values || s.currentValues))
          .map(s => {
            const currentValues = Array.isArray(s.current_values || s.currentValues) ? (s.current_values || s.currentValues) : [];
            const correctedValues = Array.isArray(s.corrected_values || s.correctedValues) ? (s.corrected_values || s.correctedValues) : [];
            return {
              current_values: currentValues,
              corrected_values: correctedValues,
              // For backwards compatibility, use first corrected value or suggested_value
              suggested_value: s.suggested_value || s.suggestedValue || (correctedValues.length > 0 ? correctedValues[0] : ''),
              reason: s.reason || s.pattern || '',
              affected_count: s.affected_count || s.affectedCount || 0,
              existing_count: s.existing_count || s.existingCount || 0,
              values_covered: s.values_covered || s.valuesCovered || currentValues.length,
            };
          });
        
        // Calculate values covered by suggestions - count actual unique values in current_values arrays
        const valuesInSuggestionsSet = new Set();
        suggestions.forEach(s => {
          (s.current_values || []).forEach(v => valuesInSuggestionsSet.add(v));
        });
        const valuesCoveredBySuggestions = valuesInSuggestionsSet.size;
        const uncategorizedCount = parsed.uncategorized_count || parsed.uncategorizedCount || 0;
        
        // Calculate values needing change (from AI or calculate from suggestions)
        let valuesNeedingChange = parsed.values_needing_change || parsed.valuesNeedingChange || 0;
        if (valuesNeedingChange === 0 && suggestions.length > 0) {
          valuesNeedingChange = valuesCoveredBySuggestions + uncategorizedCount;
        }
        
        const valuesAlreadyGood = parsed.values_already_good || parsed.valuesAlreadyGood || Math.max(0, totalUnique - valuesNeedingChange);
        
        parsed = {
          field_name: parsed.field_name || parsed.fieldName || field.name,
          total_unique_values: totalUnique,
          values_needing_change: valuesNeedingChange,
          values_already_good: valuesAlreadyGood,
          values_covered_by_suggestions: valuesCoveredBySuggestions,
          uncategorized_count: uncategorizedCount,
          good_values: parsed.good_values || parsed.goodValues || [],
          standardization_suggestions: suggestions,
          recommended_canonical_values: parsed.recommended_canonical_values || parsed.recommendedCanonicalValues || [],
          summary: parsed.summary || '',
        };
        
        // Normalize good_values
        parsed.good_values = (parsed.good_values || [])
          .filter(v => v && (v.value || v.name))
          .map(v => ({
            value: v.value || v.name || '',
            count: v.count || v.product_count || 0,
          }));
        
        // Normalize each suggestion - filter out incomplete ones
        parsed.standardization_suggestions = (parsed.standardization_suggestions || [])
          .filter(s => s && (s.current_values || s.currentValues))
          .map(s => {
            const currentVals = Array.isArray(s.current_values || s.currentValues) ? (s.current_values || s.currentValues) : [];
            const correctedVals = Array.isArray(s.corrected_values || s.correctedValues) ? (s.corrected_values || s.correctedValues) : [];
            return {
              current_values: currentVals,
              corrected_values: correctedVals,
              suggested_value: s.suggested_value || s.suggestedValue || (correctedVals.length > 0 ? correctedVals[0] : ''),
              reason: s.reason || '',
              affected_count: s.affected_count || s.affectedCount || 0,
              existing_count: s.existing_count || s.existingCount || 0,
              values_covered: s.values_covered || s.valuesCovered || currentVals.length,
            };
          });
      }

      setAnalysisResults((prev) => ({
        ...prev,
        [fieldKey]: { result, parsed, loading: false },
      }));

      // Extract recommendations from standardization_suggestions
      const newRecs = [];
      if (parsed && Array.isArray(parsed.standardization_suggestions) && parsed.standardization_suggestions.length > 0) {
        newRecs.push(
          ...buildChangeRecommendations(field, fieldKey, parsed.standardization_suggestions, 'initial')
        );
      }

      // Also surface good_values as "no change needed"
      if (parsed && Array.isArray(parsed.good_values)) {
        parsed.good_values.forEach((g, i) => {
          if (!g.value) return;
          newRecs.push({
            id: `${fieldKey}_good_${i}`,
            type: 'no_change',
            fieldKey,
            fieldName: field.name,
            fieldLabel: field.label || field.name,
            isStandardField: field.fieldType === 'standard',
            currentValues: [g.value],
            correctedValues: [g.value],
            suggestedValue: g.value,
            reason: `No standardization needed for '${g.value}'`,
            affectedCount: 0,
            existingCount: g.count || 0,
            valuesCovered: 1,
          });
        });
      }

      if (newRecs.length > 0) {
        setRecommendations((prev) => [
          ...prev.filter((r) => r.fieldKey !== fieldKey),
          ...newRecs,
        ]);
      }

      // If uncategorized values remain, automatically run full batch analysis for this field
      const goodCount = parsed?.good_values?.length || 0;
      const inSuggestions = parsed?.values_covered_by_suggestions || 0;
      const totalUnique = parsed?.total_unique_values || 0;
      const uncategorizedCount = Math.max(0, totalUnique - goodCount - inSuggestions);
      if (uncategorizedCount > 0 && !batchAnalysisState[fieldKey]?.running) {
        setTimeout(() => runFullBatchAnalysis(fieldKey), 0);
      }
    } catch (err) {
      console.error('Error analyzing field:', err);
      setAnalysisResults((prev) => ({
        ...prev,
        [fieldKey]: { result: 'Analysis failed', parsed: null, loading: false },
      }));
    }
  };
  void analyzeField;

  // Analyze uncategorized values for a field (detailed analysis)
  const analyzeUncategorizedValues = async (fieldKey) => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) return;

    const field = combinedFields.find((f) => f.key === fieldKey);
    if (!field) return;

    const existingResult = analysisResultsRef.current[fieldKey];
    if (!existingResult?.parsed) return;

    // Collect values to exclude (good values + values in suggestions)
    const excludeValues = [];
    
    // Add good values
    (existingResult.parsed.good_values || []).forEach((v) => {
      if (v.value) excludeValues.push(v.value);
    });
    
    // Add values from suggestions (both current and suggested)
    (existingResult.parsed.standardization_suggestions || []).forEach((s) => {
      (s.current_values || []).forEach((v) => excludeValues.push(v));
      if (s.suggested_value) excludeValues.push(s.suggested_value);
    });

    console.log('Sending uncategorized analysis:', {
      fieldKey,
      excludeValuesCount: excludeValues.length,
      goodValuesCount: (existingResult.parsed.good_values || []).length,
      suggestionsCount: (existingResult.parsed.standardization_suggestions || []).length,
    });

    const config = fieldConfigs[fieldKey] || {};
    const effectivePrompt = config.custom_prompt || dataQualityPrompt || '';
    const { aiEngine, aiKey, aiModel, maxRequestTokens, maxResponseTokens, aiTemperature } = getAiEngineConfig();

    setDetailedAnalysisLoading((prev) => ({ ...prev, [fieldKey]: true }));

    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/ai-analyze-uncategorized`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          field_name: field.name,
          is_standard_field: field.fieldType === 'standard',
          exclude_values: excludeValues,
          custom_prompt: effectivePrompt,
          golden_values: config.golden_values || [],
          additional_fields: config.additional_fields || [],
          ai_engine: aiEngine,
          ai_api_key: aiKey,
          ai_model: aiModel,
          max_request_tokens: maxRequestTokens,
          max_response_tokens: maxResponseTokens,
          taxonomy_root_id: dqAiSettings.taxonomy_root_id,
          ai_temperature: aiTemperature,
        }),
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText || 'Detailed analysis failed');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let result = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        result += decoder.decode(value, { stream: true });
      }

      // Parse the result
      let detailedParsed = null;
      try {
        const jsonMatch = result.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          detailedParsed = JSON.parse(jsonMatch[0]);
        }
      } catch (e) {
        // Try repair
        try {
          let repaired = result.trim();
          let braceCount = 0;
          for (const char of repaired) {
            if (char === '{') braceCount++;
            else if (char === '}') braceCount--;
          }
          while (braceCount > 0) {
            repaired += '}';
            braceCount--;
          }
          const match = repaired.match(/\{[\s\S]*\}/);
          if (match) detailedParsed = JSON.parse(match[0]);
        } catch (e2) {
          // Failed
        }
      }

      if (detailedParsed) {
        // Merge the new results with existing results
        const existingParsed = existingResult.parsed;
        
        // Add new good values
        const newGoodValues = (detailedParsed.good_values || []).map((v) => ({
          value: v.value || v.name || '',
          count: v.count || 0,
        }));
        
        // Add new suggestions - handle corrected_values array
        const newSuggestions = (detailedParsed.standardization_suggestions || []).map((s) => {
          const currentVals = s.current_values || [];
          const correctedVals = s.corrected_values || [];
          return {
            current_values: currentVals,
            corrected_values: correctedVals,
            suggested_value: s.suggested_value || (correctedVals.length > 0 ? correctedVals[0] : ''),
            reason: s.reason || '',
            affected_count: s.affected_count || 0,
            existing_count: s.existing_count || 0,
            values_covered: s.values_covered || currentVals.length,
          };
        });

        // Calculate values covered by counting actual current_values in suggestions
        const allSuggestions = [...(existingParsed.standardization_suggestions || []), ...newSuggestions];
        const allGoodValues = [...(existingParsed.good_values || []), ...newGoodValues];
        
        // Count unique values in all suggestions
        const valuesInSuggestions = new Set();
        allSuggestions.forEach(s => {
          (s.current_values || []).forEach(v => valuesInSuggestions.add(v));
        });
        
        // Update the parsed result
        const updatedParsed = {
          ...existingParsed,
          good_values: allGoodValues,
          standardization_suggestions: allSuggestions,
          values_already_good: allGoodValues.length,
          values_covered_by_suggestions: valuesInSuggestions.size,
          uncategorized_count: Math.max(0, existingParsed.total_unique_values - allGoodValues.length - valuesInSuggestions.size),
        };

        setAnalysisResults((prev) => ({
          ...prev,
          [fieldKey]: {
            ...prev[fieldKey],
            parsed: updatedParsed,
            detailedResult: result,
          },
        }));

        // Add new recommendations
        if (newSuggestions.length > 0) {
          const timestamp = Date.now();
          const newRecs = [
            ...buildChangeRecommendations(field, fieldKey, newSuggestions, `batch_${timestamp}`),
            ...newGoodValues.map((g, i) => ({
              id: `${fieldKey}_batch_good_${timestamp}_${i}`,
              type: 'no_change',
              fieldKey,
              fieldName: field.name,
              fieldLabel: field.label || field.name,
              isStandardField: field.fieldType === 'standard',
              currentValues: [g.value],
              correctedValues: [g.value],
              suggestedValue: g.value,
              reason: `No standardization needed for '${g.value}'`,
              affectedCount: 0,
              existingCount: g.count || 0,
              valuesCovered: 1,
            })),
          ];
          setRecommendations((prev) => [...prev, ...newRecs]);
        }

        // Count actual values covered by new suggestions
        const newValuesCovered = newSuggestions.reduce((sum, s) => sum + (s.current_values || []).length, 0);
        
        console.log('Batch analysis results:', {
          newGoodValues: newGoodValues.length,
          newSuggestions: newSuggestions.length,
          newValuesCovered,
          totalGoodValues: allGoodValues.length,
          totalSuggestions: allSuggestions.length,
          valuesInSuggestions: valuesInSuggestions.size,
          uncategorized: updatedParsed.uncategorized_count,
        });

        message.success(`Found ${newGoodValues.length} more good values and ${newValuesCovered} more values in ${newSuggestions.length} suggestions`);
        return { success: true, newGoodCount: newGoodValues.length, newSuggestionCount: newSuggestions.length, newValuesCovered };
      } else {
        message.warning('Could not parse detailed analysis results');
        return { success: false };
      }
    } catch (err) {
      console.error('Error in detailed analysis:', err);
      message.error('Detailed analysis failed');
      return { success: false, error: err };
    } finally {
      setDetailedAnalysisLoading((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  // Run batch analysis to categorize ALL uncategorized values for a field
  const runFullBatchAnalysis = async (fieldKey, maxBatches = 10) => {
    const field = combinedFields.find((f) => f.key === fieldKey);
    if (!field) return;

    const existingResult = analysisResultsRef.current[fieldKey];
    if (!existingResult?.parsed) {
      message.warning('Please run initial analysis first');
      return;
    }

    // Reset cancellation flag
    batchCancelledRef.current[fieldKey] = false;

    // Calculate initial uncategorized count
    const goodCount = (existingResult.parsed.good_values || []).length;
    const inSuggestions = existingResult.parsed.values_covered_by_suggestions || 0;
    const totalUnique = existingResult.parsed.total_unique_values || 0;
    let uncategorizedCount = Math.max(0, totalUnique - goodCount - inSuggestions);
    
    if (uncategorizedCount === 0) {
      message.success('All values have been categorized!');
      return;
    }

    // Estimate batches needed (roughly 50 values categorized per batch)
    const estimatedBatches = Math.min(maxBatches, Math.ceil(uncategorizedCount / 50));

    setBatchAnalysisState((prev) => ({
      ...prev,
      [fieldKey]: { running: true, currentBatch: 0, totalBatches: estimatedBatches, cancelled: false, remaining: uncategorizedCount },
    }));

    let batchNum = 0;
    let totalNewGood = 0;
    let totalNewSuggestions = 0;
    let consecutiveFailures = 0;

    while (batchNum < maxBatches && uncategorizedCount > 0) {
      // Check if cancelled using ref (immediate, not stale)
      if (batchCancelledRef.current[fieldKey]) {
        message.info('Batch analysis cancelled');
        break;
      }

      batchNum++;
      const currentBatchNum = batchNum;
      const remainingCount = uncategorizedCount;
      const nextTotalBatches = Math.min(maxBatches, currentBatchNum + Math.ceil(remainingCount / 50));
      setBatchAnalysisState((prev) => ({
        ...prev,
        [fieldKey]: { 
          ...prev[fieldKey], 
          currentBatch: currentBatchNum,
          totalBatches: nextTotalBatches,
          remaining: remainingCount,
        },
      }));

      // Run one batch of analysis
      const result = await analyzeUncategorizedValues(fieldKey);
      
      if (!result?.success) {
        consecutiveFailures++;
        if (consecutiveFailures >= 2) {
          message.error('Multiple batch failures. Stopping analysis.');
          break;
        }
        continue;
      }
      
      consecutiveFailures = 0;
      totalNewGood += result.newGoodCount || 0;
      totalNewSuggestions += result.newSuggestionCount || 0;
      
      // Update uncategorized count based on actual values found
      const newlyCategorized = (result.newGoodCount || 0) + (result.newValuesCovered || 0);
      if (newlyCategorized === 0) {
        // No progress made, AI couldn't categorize more values
        message.info('AI could not categorize more values. Some may need manual review.');
        break;
      }
      
      uncategorizedCount = Math.max(0, uncategorizedCount - newlyCategorized);

      // Small delay between batches to avoid overwhelming the API
      await new Promise((r) => setTimeout(r, 1500));
    }

    setBatchAnalysisState((prev) => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], running: false },
    }));

    if (totalNewGood > 0 || totalNewSuggestions > 0) {
      message.success(`Batch analysis complete! Found ${totalNewGood} more good values and ${totalNewSuggestions} more suggestions across ${batchNum} batches.`);
    } else {
      message.info(`Analyzed ${batchNum} batches but no additional categorizations found.`);
    }
  };

  const runFullBatchForUncategorizedFields = async () => {
    if (!fieldsWithUncategorized.length) {
      message.info('All values are already categorized.');
      return;
    }

    for (let i = 0; i < fieldsWithUncategorized.length; i++) {
      const { fieldKey, uncategorizedCount } = fieldsWithUncategorized[i];
      if (batchAnalysisState[fieldKey]?.running) continue;

      const estimatedBatches = Math.max(1, Math.ceil(uncategorizedCount / 50));
      await runFullBatchAnalysis(fieldKey, estimatedBatches);
      await new Promise((r) => setTimeout(r, 500));
    }
  };

  useEffect(() => {
    if (!analysisJobId) return;

    let cancelled = false;
    const poll = async () => {
      const headers = getAuthHeaders();
      if (!headers['X-API-Key'] && !headers['Authorization']) return;

      try {
        const resp = await fetch(
          `${API_BASE_URL}/api/v1/data-quality/analysis-jobs/${analysisJobId}`,
          { headers }
        );
        if (!resp.ok) {
          const errorText = await resp.text();
          throw new Error(errorText || 'Failed to fetch job status');
        }

        const data = await resp.json();
        if (cancelled) return;

        setAnalysisJobStatus(data);
        processAnalysisJobFields(data.fields || {});

        const runningFieldEntry = Object.entries(data.fields || {}).find(
          ([, info]) => info?.status === 'running'
        );
        const currentFieldName = runningFieldEntry?.[1]?.field_name || '';
        setBatchProgress({
          current: data.current || 0,
          total: data.total || 0,
          currentField: currentFieldName,
        });

        const terminalStatuses = ['completed', 'completed_with_errors', 'cancelled'];
        if (terminalStatuses.includes(data.status)) {
          setAnalysisStep(2);
          if (!analysisJobCompletedRef.current) {
            analysisJobCompletedRef.current = true;
            setTimeout(() => runFullBatchForUncategorizedFields(), 0);
          }
          setAnalysisJobId(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error polling analysis job:', err);
        }
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisJobId, getAuthHeaders, processAnalysisJobFields]);

  // Cancel batch analysis
  const cancelBatchAnalysis = (fieldKey) => {
    batchCancelledRef.current[fieldKey] = true;
    setBatchAnalysisState((prev) => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], cancelled: true, running: false },
    }));
  };

  // Run batch analysis for selected fields (manual trigger)
  const runBatchAnalysis = async (fieldsOverride = null) => {
    const isEventArg =
      fieldsOverride &&
      typeof fieldsOverride === 'object' &&
      typeof fieldsOverride.preventDefault === 'function';
    const fieldKeysRaw = (isEventArg ? null : fieldsOverride) || selectedFields;
    const fieldKeys = Array.isArray(fieldKeysRaw)
      ? fieldKeysRaw
      : fieldKeysRaw
      ? [fieldKeysRaw]
      : [];
    const normalizedKeys = fieldKeys
      .map((key) => {
        if (key && typeof key === 'object') {
          return String(key.key || key.name || key.label || '');
        }
        return String(key);
      })
      .filter(Boolean);
    if (!fieldKeys || fieldKeys.length === 0) {
      message.warning('Please select at least one field to analyze');
      return;
    }

    setAnalysisStep(1);
    setBatchProgress({ current: 0, total: fieldKeys.length, currentField: '' });
    setRecommendations([]);
    analysisJobProcessedRef.current = {};
    analysisJobCompletedRef.current = false;

    let fieldsToAnalyze = normalizedKeys
      .map((key) =>
        allFieldsCatalog.find(
          (f) =>
            String(f.key) === key ||
            String(f.name || '') === key ||
            String(f.label || '') === key
        )
      )
      .filter(Boolean);

    if (fieldsToAnalyze.length === 0) {
      fieldsToAnalyze = normalizedKeys
        .map((key) =>
          combinedFields.find(
            (f) =>
              String(f.key) === key ||
              String(f.name || '') === key ||
              String(f.label || '') === key
          )
        )
        .filter(Boolean);
    }

    if (fieldsToAnalyze.length === 0) {
      const objectFields = fieldKeys.filter((key) => key && typeof key === 'object');
      if (objectFields.length) {
        fieldsToAnalyze = objectFields;
      }
    }

    if (fieldsToAnalyze.length === 0) {
      message.warning('No matching fields found to analyze. Please reselect fields.');
      setAnalysisStep(0);
      return;
    }

      const { aiEngine, aiKey, aiModel, maxRequestTokens, maxResponseTokens, aiTemperature } = getAiEngineConfig();
    const fieldsPayload = fieldsToAnalyze.map((field) => {
      const fieldKey = field?.key || field?.name || field?.label || '';
      const config = fieldConfigs[fieldKey] || {};
      const effectivePrompt = config.custom_prompt || dataQualityPrompt || '';
      const fieldName = field?.name || field?.key || field?.label || '';
      return {
        field_key: fieldKey,
        field_name: fieldName,
        is_standard_field: field?.fieldType === 'standard',
        custom_prompt: effectivePrompt,
        golden_values: config.golden_values || [],
        additional_fields: config.additional_fields || [],
        ai_engine: aiEngine,
        ai_api_key: aiKey,
        ai_model: aiModel,
        max_request_tokens: maxRequestTokens,
        max_response_tokens: maxResponseTokens,
        taxonomy_root_id: dqAiSettings.taxonomy_root_id,
        ai_temperature: aiTemperature,
      };
    });

    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/analysis-jobs/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fields: fieldsPayload }),
      });
      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText || 'Failed to start analysis job');
      }
      const data = await resp.json();
      setAnalysisJobId(data.job_id);
      setAnalysisJobStatus({
        id: data.job_id,
        status: 'running',
        current: 0,
        total: fieldsPayload.length,
        fields: {},
      });
    } catch (err) {
      message.error(err.message || 'Failed to start analysis job');
      setAnalysisStep(0);
    }
  };

  const cancelAnalysisJob = async () => {
    const jobId = analysisJobStatus?.id || analysisJobId;
    if (!jobId) {
      message.info('No analysis job is running');
      return;
    }
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/data-quality/analysis-jobs/${jobId}/cancel`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );
      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText || 'Failed to cancel job');
      }
      setAnalysisJobStatus((prev) =>
        prev ? { ...prev, status: 'cancelled', cancelled: true } : prev
      );
      setAnalysisJobId(null);
      message.success('Analysis job cancelled');
    } catch (err) {
      message.error(err.message || 'Failed to cancel job');
    }
  };

  // Reset analysis and go back to field selection
  const resetAnalysis = () => {
    setAnalysisStep(0);
    setAnalysisResults({});
    setRecommendations([]);
    setSelectedRecommendations([]);
    setBatchProgress({ current: 0, total: 0, currentField: '' });
  };

  // Exit analysis flow (keep results but go back to selection)
  const exitToSelection = () => {
    setAnalysisStep(0);
  };

  // Apply selected recommendations
  const applyRecommendations = async () => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) return;

    const selectedRecs = recommendations.filter((r) =>
      selectedRecommendations.includes(r.id)
    );

    const actionableRecs = selectedRecs.filter((r) => r.type !== 'no_change');

    if (selectedRecs.length === 0) {
      message.warning('Please select recommendations to apply');
      return;
    }

    if (actionableRecs.length === 0) {
      message.info('Selected items require no changes.');
      return;
    }

    setApplyingChanges(true);
    try {
      let totalApplied = 0;

      for (const rec of actionableRecs) {
        const resp = await fetch(`${API_BASE_URL}/api/v1/data-quality/bulk-standardize`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            field_name: rec.fieldName,
            is_standard_field: rec.isStandardField,
            old_values: rec.currentValues,
            new_value: recGroupOverrides[rec.id] ?? rec.suggestedValue,
          }),
        });

        if (resp.ok) {
          const result = await resp.json();
          totalApplied += result.updated_count || 0;
        }
      }

      message.success(`Applied changes to ${totalApplied} products`);
      setSelectedRecommendations([]);
      fetchSummary();
      fetchFields();
    } catch (err) {
      console.error('Error applying recommendations:', err);
      message.error('Failed to apply some recommendations');
    } finally {
      setApplyingChanges(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchSummary();
      fetchFields();
      fetchDataQualityConfig();
      fetchTaxonomyRoots();
    }
  }, [authLoading, isAuthenticated, fetchSummary, fetchFields, fetchDataQualityConfig, fetchTaxonomyRoots]);


  // Load issues when tab changes
  useEffect(() => {
    if (activeTab === 'issues' && !authLoading && isAuthenticated) {
      fetchProductsWithIssues();
    }
  }, [activeTab, authLoading, isAuthenticated, fetchProductsWithIssues]);

  useEffect(() => {
    if (activeTab !== 'issues' || authLoading || !isAuthenticated) return;
    const timer = setTimeout(() => {
      setIssuesPage(1);
      fetchProductsWithIssues({
        issueType: issueTypeFilter,
        search: issuesSearch,
        page: 1,
        pageSize: issuesPageSize,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [activeTab, authLoading, isAuthenticated, issueTypeFilter, issuesSearch, issuesPageSize, fetchProductsWithIssues]);

  // Load ambiguous mappings when the tab is opened
  useEffect(() => {
    if (activeTab === 'ambiguous-mappings' && !authLoading && isAuthenticated) {
      fetchAmbiguousItems();
    }
  }, [activeTab, authLoading, isAuthenticated, fetchAmbiguousItems]);

  useEffect(() => {
    if (activeTab !== 'ambiguous-mappings' || authLoading || !isAuthenticated) return;
    const timer = setTimeout(() => {
      setAmbiguousPage(1);
      fetchAmbiguousItems({
        mode: ambiguousMode,
        search: ambiguousSearch,
        page: 1,
        pageSize: ambiguousPageSize,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [activeTab, authLoading, isAuthenticated, ambiguousMode, ambiguousSearch, ambiguousPageSize, fetchAmbiguousItems]);

  useEffect(() => {
    if (selectedIssueKeys.length || selectedIssueRows.length) {
      setSelectedIssueKeys([]);
      setSelectedIssueRows([]);
    }
  }, [productsWithIssues, selectedIssueKeys.length, selectedIssueRows.length]);

  if (authLoading) {
    return (
      <div className="text-center py-5">
        <Spin size="large" />
      </div>
    );
  }

  // Quality score color
  const getScoreColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    return '#f5222d';
  };

  // Severity tag
  const getSeverityTag = (severity) => {
    const config = {
      critical: { color: 'red', icon: <ExclamationCircleOutlined /> },
      warning: { color: 'orange', icon: <WarningOutlined /> },
      info: { color: 'blue', icon: <InfoCircleOutlined /> },
    };
    const cfg = config[severity] || config.info;
    return (
      <Tag color={cfg.color} icon={cfg.icon}>
        {severity?.toUpperCase() || 'INFO'}
      </Tag>
    );
  };

  // Products with issues columns
  const issuesColumns = [
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      key: 'product_code',
      width: 120,
      render: (value) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            loadStandardizeDetail(value);
          }}
        >
          {value || '—'}
        </Button>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name, record) => (
        <span>
          {name || <Text type="danger">Missing</Text>}
          {record.missing_name === 1 && <Badge status="error" style={{ marginLeft: 8 }} />}
        </span>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat, record) => (
        <span>
          {cat || <Text type="warning">Missing</Text>}
          {record.missing_category === 1 && <Badge status="warning" style={{ marginLeft: 8 }} />}
        </span>
      ),
    },
    {
      title: 'Issues',
      key: 'issues',
      render: (_, record) => (
        <Space wrap size={[4, 4]}>
          {record.missing_name === 1 && <Tag color="red">No Name</Tag>}
          {record.missing_category === 1 && <Tag color="orange">No Category</Tag>}
          {record.missing_manufacturer === 1 && <Tag color="orange">No Manufacturer</Tag>}
          {record.no_attributes === 1 && <Tag color="blue">No Attributes</Tag>}
        </Space>
      ),
    },
  ];

  // Render field selection card
  const renderFieldCard = (field) => {
    const isSelected = selectedFields.includes(field.key);
    const hasResult = analysisResults[field.key];
    const isAnalyzing = hasResult?.loading;
    const configuredContextFields = (fieldConfigs[field.key]?.additional_fields || []).filter(
      (key) => key !== field.key
    );
    const hasInstruction =
      !!(fieldConfigs[field.key]?.custom_prompt || '').trim() ||
      configuredContextFields.length > 0;
    const isStandard = field.fieldType === 'standard';

    return (
      <Col xs={24} sm={12} md={8} lg={6} key={field.key}>
        <Card
          size="small"
          className={`field-card ${isSelected ? 'selected' : ''}`}
          style={{
            border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onClick={() => toggleField(field.key)}
          hoverable
        >
          <div className="d-flex justify-content-between align-items-start mb-2">
            <Space>
              <Checkbox checked={isSelected} onClick={(e) => e.stopPropagation()} onChange={() => toggleField(field.key)} />
              {isStandard ? (
                <Tag color="purple" style={{ fontSize: 10 }}>
                  Standard
                </Tag>
              ) : (
                <Tag color="cyan" style={{ fontSize: 10 }}>
                  Attribute
                </Tag>
              )}
            </Space>
            <Space size={4}>
              {hasInstruction && (
                <Tooltip title="Has AI instructions or context fields">
                  <BulbOutlined style={{ color: '#faad14', fontSize: 14 }} />
                </Tooltip>
              )}
              <Tooltip title="Configure AI prompt, context fields, and golden values">
                <Button
                  type="text"
                  size="small"
                  icon={<SettingOutlined />}
                  onClick={(e) => openFieldConfig(field, e)}
                  style={{ padding: 2 }}
                />
              </Tooltip>
              <Tooltip title="Clear this field across all records">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  loading={!!clearFieldLoadingMap[field.key]}
                  onClick={(e) => clearFieldAcrossAllRecords(field, e)}
                  style={{ padding: 2 }}
                />
              </Tooltip>
              {hasResult && !isAnalyzing && (
                <Tag color={hasResult.parsed ? 'green' : 'default'} style={{ fontSize: 10 }}>
                  Done
                </Tag>
              )}
              {isAnalyzing && <Spin size="small" />}
            </Space>
          </div>
          <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
            {field.label || field.name}
          </Text>
          {field.description && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
              {field.description}
            </Text>
          )}
          <div style={{ fontSize: 12, color: '#666' }}>
            <span>{field.total_count || 0} products</span>
            <span style={{ margin: '0 8px' }}>•</span>
            <span>{field.unique_values || 0} unique</span>
          </div>
          {(field.empty_count || 0) > 0 && (
            <Tag color="orange" style={{ marginTop: 4, fontSize: 10 }}>
              {field.empty_count} empty
            </Tag>
          )}
          {field.is_required && (
            <Tag color="red" style={{ marginTop: 4, fontSize: 10 }}>
              Required
            </Tag>
          )}
          {configuredContextFields.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <Tooltip
                title={configuredContextFields
                  .map((key) => fieldLabelByKey[key] || key)
                  .join(', ')}
              >
                <Tag color="blue" style={{ fontSize: 10 }}>
                  AI Context: {configuredContextFields.length} field
                  {configuredContextFields.length > 1 ? 's' : ''}
                </Tag>
              </Tooltip>
              <div style={{ marginTop: 2 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {configuredContextFields
                    .slice(0, 2)
                    .map((key) => fieldLabelByKey[key] || key)
                    .join(', ')}
                  {configuredContextFields.length > 2
                    ? ` +${configuredContextFields.length - 2} more`
                    : ''}
                </Text>
              </div>
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Top: {(field.sample_values || []).slice(0, 2).map((v) => v.value).join(', ')}
            </Text>
          </div>
        </Card>
      </Col>
    );
  };

  // Render analysis results
  const renderAnalysisResults = () => {
    const analyzedFields = selectedFields.filter((key) => analysisResults[key]);

    if (analyzedFields.length === 0) {
      return <Empty description="No analysis results yet" />;
    }

    return (
      <div>
        {analyzedFields.map((fieldKey) => {
          const result = analysisResults[fieldKey];
          const parsed = result?.parsed;
          const field = combinedFields.find((f) => f.key === fieldKey);

          return (
            <Card
              key={fieldKey}
              size="small"
              className="mb-3"
              title={
                <Space>
                  {field?.fieldType === 'standard' ? (
                    <TagOutlined style={{ color: '#722ed1' }} />
                  ) : (
                    <FormOutlined style={{ color: '#13c2c2' }} />
                  )}
                  <span>{field?.label || field?.name || fieldKey}</span>
                  {parsed && (() => {
                    const goodCount = (parsed.good_values || []).length;
                    const needReview = parsed.total_unique_values - goodCount;
                    return (
                      <Tag color={needReview > 0 ? 'orange' : 'green'}>
                        {goodCount} good / {needReview} to review
                      </Tag>
                    );
                  })()}
                </Space>
              }
            >
              {result.loading ? (
                <div className="text-center py-3">
                  <Spin />
                  <div>
                    <Text type="secondary">Analyzing...</Text>
                  </div>
                </div>
              ) : parsed ? (
                <div>
                  <Row gutter={[16, 16]}>
                    <Col xs={12} sm={6}>
                      <Statistic
                        title="Total Unique Values"
                        value={parsed.total_unique_values || 0}
                        prefix={<UnorderedListOutlined />}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Statistic
                        title="Already Good"
                        value={(parsed.good_values || []).length}
                        valueStyle={{ color: '#52c41a' }}
                        prefix={<CheckCircleOutlined />}
                        suffix={
                          parsed.total_unique_values > 0 ? (
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                              ({Math.round(((parsed.good_values || []).length / parsed.total_unique_values) * 100)}%)
                            </span>
                          ) : null
                        }
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      {(() => {
                        const goodCount = (parsed.good_values || []).length;
                        const needChanges = parsed.total_unique_values - goodCount;
                        return (
                          <Statistic
                            title="Need Review"
                            value={needChanges}
                            valueStyle={{
                              color: needChanges > 0 ? '#faad14' : '#52c41a',
                            }}
                            prefix={
                              needChanges > 0 ? (
                                <WarningOutlined />
                              ) : (
                                <CheckCircleOutlined />
                              )
                            }
                            suffix={
                              parsed.total_unique_values > 0 && needChanges > 0 ? (
                                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                  ({Math.round((needChanges / parsed.total_unique_values) * 100)}%)
                                </span>
                              ) : null
                            }
                          />
                        );
                      })()}
                    </Col>
                    <Col xs={12} sm={6}>
                      <Statistic
                        title="Suggestions"
                        value={(parsed.standardization_suggestions || []).length}
                        prefix={<BulbOutlined />}
                      />
                    </Col>
                  </Row>
                  
                  {/* Visual breakdown bar */}
                  {parsed.total_unique_values > 0 && (() => {
                    const goodCount = (parsed.good_values || []).length;
                    const inSuggestions = parsed.values_covered_by_suggestions || 0;
                    const uncategorized = Math.max(0, parsed.total_unique_values - goodCount - inSuggestions);
                    
                    return (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ display: 'flex', height: 12, borderRadius: 4, overflow: 'hidden', border: '1px solid #d9d9d9' }}>
                          <Tooltip title={`${goodCount} good values (no changes needed)`}>
                            <div
                              style={{
                                width: `${(goodCount / parsed.total_unique_values) * 100}%`,
                                backgroundColor: '#52c41a',
                                transition: 'width 0.3s',
                              }}
                            />
                          </Tooltip>
                          <Tooltip title={`${inSuggestions} values covered by suggestions below`}>
                            <div
                              style={{
                                width: `${(inSuggestions / parsed.total_unique_values) * 100}%`,
                                backgroundColor: '#1890ff',
                                transition: 'width 0.3s',
                              }}
                            />
                          </Tooltip>
                          {uncategorized > 0 && (
                            <Tooltip title={`${uncategorized} values not yet categorized`}>
                              <div
                                style={{
                                  width: `${(uncategorized / parsed.total_unique_values) * 100}%`,
                                  backgroundColor: '#faad14',
                                  transition: 'width 0.3s',
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, flexWrap: 'wrap', gap: 8 }}>
                          <Text type="secondary">
                            <span style={{ color: '#52c41a' }}>■</span> Good ({goodCount})
                          </Text>
                          <Text type="secondary">
                            <span style={{ color: '#1890ff' }}>■</span> In Suggestions ({inSuggestions})
                          </Text>
                          {uncategorized > 0 && (
                            <Text type="secondary">
                              <span style={{ color: '#faad14' }}>■</span> Uncategorized ({uncategorized})
                            </Text>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {parsed.summary && (
                    <Alert
                      message={parsed.summary}
                      type="info"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}

                  {/* Good Values Section */}
                  {parsed.good_values && parsed.good_values.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <Text strong style={{ color: '#52c41a' }}>
                        <CheckCircleOutlined style={{ marginRight: 8 }} />
                        Good Values (No Changes Needed):
                      </Text>
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {parsed.good_values.slice(0, 10).map((v, i) => (
                          <Tooltip key={i} title={`${v.count} products`}>
                            <Tag
                              color="green"
                              style={{ margin: 2, cursor: 'default' }}
                            >
                              {v.value}
                              <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 11 }}>
                                ({v.count})
                              </span>
                            </Tag>
                          </Tooltip>
                        ))}
                        {parsed.good_values.length > 10 && (
                          <Collapse
                            ghost
                            style={{ width: '100%', marginTop: 4 }}
                            items={[
                              {
                                key: '1',
                                label: (
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    +{parsed.good_values.length - 10} more good values
                                  </Text>
                                ),
                                children: (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {parsed.good_values.slice(10).map((v, i) => (
                                      <Tooltip key={i} title={`${v.count} products`}>
                                        <Tag color="green" style={{ margin: 2 }}>
                                          {v.value}
                                          <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 11 }}>
                                            ({v.count})
                                          </span>
                                        </Tag>
                                      </Tooltip>
                                    ))}
                                  </div>
                                ),
                              },
                            ]}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {parsed.recommended_canonical_values &&
                    parsed.recommended_canonical_values.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <Text strong>
                          <StarOutlined style={{ marginRight: 8, color: '#faad14' }} />
                          Recommended Canonical Values:
                        </Text>
                        <div style={{ marginTop: 8 }}>
                          {parsed.recommended_canonical_values.slice(0, 15).map((val, i) => (
                            <Tag key={i} color="blue" style={{ margin: '2px' }}>
                              {val}
                            </Tag>
                          ))}
                          {parsed.recommended_canonical_values.length > 15 && (
                            <Tag>+{parsed.recommended_canonical_values.length - 15} more</Tag>
                          )}
                        </div>
                      </div>
                    )}

                  {parsed.standardization_suggestions &&
                    parsed.standardization_suggestions.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Text strong>
                            <BulbOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                            Standardization Suggestions:
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {parsed.standardization_suggestions.reduce((sum, s) => sum + (s.values_covered || s.current_values?.length || 0), 0)} values covered
                          </Text>
                        </div>
                        <List
                          size="small"
                          dataSource={parsed.standardization_suggestions.slice(0, 5)}
                          renderItem={(s, idx) => (
                            <List.Item style={{ background: idx % 2 === 0 ? '#fafafa' : '#fff', padding: '8px 12px', borderRadius: 4 }}>
                              <div style={{ width: '100%' }}>
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <Tag color="blue" style={{ fontSize: 11 }}>
                                    {s.values_covered || s.current_values?.length || 0} value{(s.values_covered || s.current_values?.length || 0) !== 1 ? 's' : ''}
                                  </Tag>
                                  <Space size={4}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                      {s.affected_count || 0} products to update
                                    </Text>
                                    {s.existing_count > 0 && (
                                      <Tag color="green" style={{ fontSize: 10 }}>
                                        {s.existing_count} already correct
                                      </Tag>
                                    )}
                                  </Space>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {/* Show value mappings if corrected_values exists */}
                                  {(s.corrected_values && s.corrected_values.length > 0) ? (
                                    <>
                                      {(s.current_values || []).slice(0, 5).map((v, i) => (
                                        <div key={i} className="d-flex align-items-center gap-2">
                                          <Tag color="red" style={{ margin: 0, minWidth: 150 }}>{v}</Tag>
                                          <ArrowRightOutlined style={{ fontSize: 10, color: '#999' }} />
                                          <Tag color="green" style={{ margin: 0 }}>{s.corrected_values[i] || s.suggested_value}</Tag>
                                        </div>
                                      ))}
                                      {(s.current_values || []).length > 5 && (
                                        <Tooltip 
                                          title={
                                            <div>
                                              {(s.current_values || []).slice(5).map((v, i) => (
                                                <div key={i}>{v} → {s.corrected_values[i + 5] || s.suggested_value}</div>
                                              ))}
                                            </div>
                                          }
                                        >
                                          <Text type="secondary" style={{ cursor: 'help', fontSize: 11 }}>
                                            +{(s.current_values || []).length - 5} more mappings...
                                          </Text>
                                        </Tooltip>
                                      )}
                                    </>
                                  ) : (
                                    /* Fallback for old format - all values map to single suggested_value */
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                      {(s.current_values || []).slice(0, 5).map((v, i) => (
                                        <Tag key={i} color="red" style={{ margin: 2 }}>{v}</Tag>
                                      ))}
                                      {(s.current_values || []).length > 5 && (
                                        <Tooltip title={(s.current_values || []).slice(5).join(', ')}>
                                          <Tag color="red" style={{ margin: 2, cursor: 'help' }}>
                                            +{(s.current_values || []).length - 5} more
                                          </Tag>
                                        </Tooltip>
                                      )}
                                      <ArrowRightOutlined />
                                      <Tag color="green">{s.suggested_value}</Tag>
                                    </div>
                                  )}
                                </div>
                                {s.reason && (
                                  <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                                    {s.reason}
                                  </Text>
                                )}
                              </div>
                            </List.Item>
                          )}
                        />
                        {parsed.standardization_suggestions.length > 5 && (
                          <Collapse
                            ghost
                            style={{ marginTop: 8 }}
                            items={[
                              {
                                key: '1',
                                label: (
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    +{parsed.standardization_suggestions.length - 5} more suggestions 
                                    ({parsed.standardization_suggestions.slice(5).reduce((sum, s) => sum + (s.values_covered || s.current_values?.length || 0), 0)} more values)
                                  </Text>
                                ),
                                children: (
                                  <List
                                    size="small"
                                    dataSource={parsed.standardization_suggestions.slice(5)}
                                    renderItem={(s, idx) => (
                                      <List.Item style={{ background: idx % 2 === 0 ? '#fafafa' : '#fff', padding: '8px 12px', borderRadius: 4 }}>
                                        <div style={{ width: '100%' }}>
                                          <div className="d-flex justify-content-between align-items-start mb-1">
                                            <Tag color="blue" style={{ fontSize: 11 }}>
                                              {s.values_covered || s.current_values?.length || 0} value{(s.values_covered || s.current_values?.length || 0) !== 1 ? 's' : ''}
                                            </Tag>
                                            <Space size={4}>
                                              <Text type="secondary" style={{ fontSize: 11 }}>
                                                {s.affected_count || 0} products
                                              </Text>
                                              {s.existing_count > 0 && (
                                                <Tag color="green" style={{ fontSize: 10 }}>
                                                  {s.existing_count} correct
                                                </Tag>
                                              )}
                                            </Space>
                                          </div>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {(s.corrected_values && s.corrected_values.length > 0) ? (
                                              <>
                                                {(s.current_values || []).slice(0, 3).map((v, i) => (
                                                  <div key={i} className="d-flex align-items-center gap-2">
                                                    <Tag color="red" style={{ margin: 0 }}>{v}</Tag>
                                                    <ArrowRightOutlined style={{ fontSize: 10, color: '#999' }} />
                                                    <Tag color="green" style={{ margin: 0 }}>{s.corrected_values[i] || s.suggested_value}</Tag>
                                                  </div>
                                                ))}
                                                {(s.current_values || []).length > 3 && (
                                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                                    +{(s.current_values || []).length - 3} more mappings
                                                  </Text>
                                                )}
                                              </>
                                            ) : (
                                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                                {(s.current_values || []).slice(0, 3).map((v, i) => (
                                                  <Tag key={i} color="red" style={{ margin: 2 }}>{v}</Tag>
                                                ))}
                                                {(s.current_values || []).length > 3 && (
                                                  <Tag color="red" style={{ margin: 2 }}>+{(s.current_values || []).length - 3}</Tag>
                                                )}
                                                <ArrowRightOutlined />
                                                <Tag color="green">{s.suggested_value}</Tag>
                                              </div>
                                            )}
                                          </div>
                                          {s.reason && (
                                            <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                                              {s.reason}
                                            </Text>
                                          )}
                                        </div>
                                      </List.Item>
                                    )}
                                  />
                                ),
                              },
                            ]}
                          />
                        )}
                        
                        {/* Show uncategorized count if any */}
                        {(() => {
                          const goodCount = (parsed.good_values || []).length;
                          const inSuggestions = parsed.values_covered_by_suggestions || 0;
                          const uncategorizedCount = Math.max(0, parsed.total_unique_values - goodCount - inSuggestions);
                          
                          if (uncategorizedCount > 0) {
                            const batchState = batchAnalysisState[fieldKey];
                            const isRunningBatch = batchState?.running;
                            
                            return (
                              <Card size="small" style={{ marginTop: 12, background: '#fffbe6', border: '1px solid #ffe58f' }}>
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                                  <div>
                                    <Text strong style={{ color: '#d48806' }}>
                                      <WarningOutlined style={{ marginRight: 8 }} />
                                      {uncategorizedCount} values not yet categorized
                                    </Text>
                                    <div style={{ marginTop: 4 }}>
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        {goodCount} good, {inSuggestions} in suggestions
                                      </Text>
                                    </div>
                                    {isRunningBatch && (
                                      <div style={{ marginTop: 8 }}>
                                        <Progress
                                          percent={Math.round((batchState.currentBatch / batchState.totalBatches) * 100)}
                                          size="small"
                                          status="active"
                                          style={{ width: 200 }}
                                        />
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                          Batch {batchState.currentBatch} of ~{batchState.totalBatches}
                                          {batchState.remaining && ` (${batchState.remaining} remaining)`}
                                        </Text>
                                      </div>
                                    )}
                                  </div>
                                  <Space wrap>
                                    {!isRunningBatch ? (
                                      <>
                                        <Tooltip title="Analyze one batch of uncategorized values">
                                          <Button
                                            size="small"
                                            icon={<SearchOutlined />}
                                            loading={detailedAnalysisLoading[fieldKey]}
                                            onClick={() => analyzeUncategorizedValues(fieldKey)}
                                          >
                                            Analyze Next Batch
                                          </Button>
                                        </Tooltip>
                                        <Tooltip title="Automatically analyze all remaining values in multiple batches">
                                          <Button
                                            size="small"
                                            type="primary"
                                            icon={<ThunderboltOutlined />}
                                            onClick={() => runFullBatchAnalysis(fieldKey)}
                                          >
                                            Analyze All ({uncategorizedCount})
                                          </Button>
                                        </Tooltip>
                                      </>
                                    ) : (
                                      <Button
                                        size="small"
                                        danger
                                        icon={<CloseCircleOutlined />}
                                        onClick={() => cancelBatchAnalysis(fieldKey)}
                                      >
                                        Cancel
                                      </Button>
                                    )}
                                  </Space>
                                </div>
                              </Card>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                </div>
              ) : (
                <div>
                  <Alert
                    message="AI Analysis Response"
                    description="The AI response could not be fully parsed. Showing raw response below."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <Card
                    size="small"
                    style={{
                      background: '#fafafa',
                      maxHeight: 400,
                      overflow: 'auto',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {(result.result || 'No result').split('\n').map((line, i) => {
                        // Try to make the output more readable
                        const trimmedLine = line.trim();
                        
                        // Highlight lines that look like suggestions
                        if (trimmedLine.includes('->') || trimmedLine.includes('→')) {
                          return (
                            <div key={i} style={{ background: '#e6f7ff', padding: '4px 8px', margin: '4px 0', borderRadius: 4 }}>
                              <ArrowRightOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                              {line}
                            </div>
                          );
                        }
                        
                        // Highlight issue/warning lines
                        if (trimmedLine.toLowerCase().includes('issue') || trimmedLine.toLowerCase().includes('problem')) {
                          return (
                            <div key={i} style={{ background: '#fff7e6', padding: '4px 8px', margin: '4px 0', borderRadius: 4 }}>
                              <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                              {line}
                            </div>
                          );
                        }
                        
                        // Highlight recommendation lines
                        if (trimmedLine.toLowerCase().includes('recommend') || trimmedLine.toLowerCase().includes('suggest')) {
                          return (
                            <div key={i} style={{ background: '#f6ffed', padding: '4px 8px', margin: '4px 0', borderRadius: 4 }}>
                              <BulbOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                              {line}
                            </div>
                          );
                        }
                        
                        // Regular line
                        return <div key={i}>{line || '\u00A0'}</div>;
                      })}
                    </div>
                  </Card>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  };

  // Render recommendations panel
  const renderRecommendations = () => {
    if (recommendations.length === 0) {
      return (
        <Empty description="No recommendations yet" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Text type="secondary">Run AI analysis on fields to get recommendations</Text>
        </Empty>
      );
    }

    // Group by field
    const groupedRecs = {};
    recommendations.forEach((rec) => {
      if (!groupedRecs[rec.fieldKey]) {
        groupedRecs[rec.fieldKey] = {
          fieldLabel: rec.fieldLabel,
          isStandardField: rec.isStandardField,
          items: [],
        };
      }
      groupedRecs[rec.fieldKey].items.push(rec);
    });

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Space>
            <Checkbox
              checked={selectedRecommendations.length === recommendations.length}
              indeterminate={
                selectedRecommendations.length > 0 &&
                selectedRecommendations.length < recommendations.length
              }
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedRecommendations(recommendations.map((r) => r.id));
                } else {
                  setSelectedRecommendations([]);
                }
              }}
            >
              Select All ({recommendations.length})
            </Checkbox>
            <Text type="secondary">{selectedRecommendations.length} selected</Text>
          </Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={applyRecommendations}
            loading={applyingChanges}
            disabled={selectedRecommendations.length === 0}
          >
            Apply Selected ({selectedRecommendations.length})
          </Button>
        </div>

        {Object.entries(groupedRecs).map(([fieldKey, group]) => (
          <Card
            key={fieldKey}
            size="small"
            className="mb-3"
            title={
              <Space>
                {group.isStandardField ? (
                  <Tag color="purple">Standard</Tag>
                ) : (
                  <Tag color="cyan">Attribute</Tag>
                )}
                <span>{group.fieldLabel}</span>
                <Badge count={group.items.length} style={{ backgroundColor: '#1890ff' }} />
              </Space>
            }
          >
            {(() => {
              const noChange = group.items.filter((rec) => rec.type === 'no_change');
              const changeItems = group.items.filter((rec) => rec.type !== 'no_change');

              const suggestionGroups = {};
              changeItems.forEach((rec) => {
                const key = `${rec.suggestedValue || ''}`;
                if (!suggestionGroups[key]) {
                  suggestionGroups[key] = {
                    suggestedValue: rec.suggestedValue,
                    reasons: new Set(),
                    currentValues: [],
                    ids: [],
                    affectedCount: 0,
                    existingCount: 0,
                  };
                }
                if (rec.reason) {
                  suggestionGroups[key].reasons.add(rec.reason);
                }
                suggestionGroups[key].currentValues.push(...(rec.currentValues || []));
                suggestionGroups[key].ids.push(rec.id);
                suggestionGroups[key].affectedCount += rec.affectedCount || 0;
                suggestionGroups[key].existingCount += rec.existingCount || 0;
              });

              const changeCards = Object.values(suggestionGroups).map((groupItem, idx) => {
                const uniqueCurrent = Array.from(new Set(groupItem.currentValues || []));
                const reasonText = Array.from(groupItem.reasons || []).join(' | ');
                const groupChecked = groupItem.ids.every((id) => selectedRecommendations.includes(id));
                const groupIndeterminate =
                  groupItem.ids.some((id) => selectedRecommendations.includes(id)) && !groupChecked;
                const recKey = `${fieldKey}::${groupItem.suggestedValue || 'no_value'}::${uniqueCurrent.join('|')}`;
                const recState = recProductsByKey[recKey] || {};
                const productList = recState.products || [];
                const aiUpdatedCount = productList.reduce((sum, item) => {
                  const status = getAiStandardizeStatus(item.ai_standardized);
                  return sum + (status.hasData ? 1 : 0);
                }, 0);
                return (
                  <Card
                    key={`${group.fieldLabel}_${groupItem.suggestedValue}_${idx}`}
                    size="small"
                    className="mb-3"
                    style={{ background: groupChecked ? '#e6f7ff' : '#fff' }}
                    bodyStyle={{ padding: 12 }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <Checkbox
                        checked={groupChecked}
                        indeterminate={groupIndeterminate}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRecommendations((prev) => Array.from(new Set([...prev, ...groupItem.ids])));
                          } else {
                            setSelectedRecommendations((prev) =>
                              prev.filter((id) => !groupItem.ids.includes(id))
                            );
                          }
                        }}
                      />
                      <Space size={4}>
                        <Tooltip title="Products that will be updated">
                          <Tag color="orange">{`${groupItem.affectedCount} to change`}</Tag>
                        </Tooltip>
                        {groupItem.existingCount > 0 && (
                          <Tooltip title="Products already using the suggested value">
                            <Tag color="green">
                              <CheckCircleOutlined style={{ marginRight: 2 }} />
                              {groupItem.existingCount} already correct
                            </Tag>
                          </Tooltip>
                        )}
                        {productList.length > 0 && (
                          <Tooltip title="Products with AI standardized data">
                            <Tag color={aiUpdatedCount > 0 ? 'blue' : 'default'}>
                              AI updated {aiUpdatedCount}/{productList.length}
                            </Tag>
                          </Tooltip>
                        )}
                      </Space>
                    </div>
                    <div style={{ marginLeft: 24, marginTop: 4 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {uniqueCurrent.map((v) => (
                          <Tag key={v} color="red" style={{ margin: 2 }}>
                            {v}
                          </Tag>
                        ))}
                        <ArrowRightOutlined />
                        {recGroupEditing[recKey] ? (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                            {group.isStandardField ? (
                              <Input
                                size="small"
                                autoFocus
                                defaultValue={recGroupOverrides[groupItem.ids[0]] ?? groupItem.suggestedValue}
                                style={{ width: 200 }}
                                onPressEnter={(e) => {
                                  const val = e.target.value.trim() || groupItem.suggestedValue;
                                  setRecGroupEditing((prev) => ({ ...prev, [recKey]: false }));
                                  setRecGroupOverrides((prev) => {
                                    const next = { ...prev };
                                    groupItem.ids.forEach((id) => { next[id] = val; });
                                    return next;
                                  });
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value.trim() || groupItem.suggestedValue;
                                  setRecGroupEditing((prev) => ({ ...prev, [recKey]: false }));
                                  setRecGroupOverrides((prev) => {
                                    const next = { ...prev };
                                    groupItem.ids.forEach((id) => { next[id] = val; });
                                    return next;
                                  });
                                }}
                              />
                            ) : (
                              <RecValueEditor
                                value={recGroupOverrides[groupItem.ids[0]] ?? groupItem.suggestedValue}
                                isAttribute={true}
                                overridden={
                                  recGroupOverrides[groupItem.ids[0]] !== undefined &&
                                  JSON.stringify(recGroupOverrides[groupItem.ids[0]]) !== JSON.stringify(groupItem.suggestedValue)
                                }
                                onChange={(v) =>
                                  setRecGroupOverrides((prev) => {
                                    const next = { ...prev };
                                    groupItem.ids.forEach((id) => { next[id] = v; });
                                    return next;
                                  })
                                }
                              />
                            )}
                            <Tooltip title="Done editing">
                              <Button
                                type="text"
                                size="small"
                                icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                onClick={() => setRecGroupEditing((prev) => ({ ...prev, [recKey]: false }))}
                                style={{ padding: 2, marginTop: 1 }}
                              />
                            </Tooltip>
                          </div>
                        ) : (
                          (() => {
                            const effectiveValue = recGroupOverrides[groupItem.ids[0]] ?? groupItem.suggestedValue;
                            const isOverridden = recGroupOverrides[groupItem.ids[0]] !== undefined &&
                              JSON.stringify(recGroupOverrides[groupItem.ids[0]]) !== JSON.stringify(groupItem.suggestedValue);
                            const tagColor = isOverridden ? 'orange' : 'green';
                            return (
                              <Space size={4} wrap>
                                {Array.isArray(effectiveValue)
                                  ? effectiveValue.map((v, i) => (
                                      <Tag key={i} color={tagColor}>{String(v)}</Tag>
                                    ))
                                  : <Tag color={tagColor}>{effectiveValue}</Tag>
                                }
                                <Tooltip title="Edit suggested value">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => setRecGroupEditing((prev) => ({ ...prev, [recKey]: true }))}
                                    style={{ padding: 0, height: 'auto' }}
                                  />
                                </Tooltip>
                                {isOverridden && (
                                  <Tooltip title="Reset to AI suggestion">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<RollbackOutlined />}
                                      onClick={() =>
                                        setRecGroupOverrides((prev) => {
                                          const next = { ...prev };
                                          groupItem.ids.forEach((id) => { delete next[id]; });
                                          return next;
                                        })
                                      }
                                      style={{ padding: 0, height: 'auto', color: '#8c8c8c' }}
                                    />
                                  </Tooltip>
                                )}
                              </Space>
                            );
                          })()
                        )}
                      </div>
                      {reasonText && (
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <BulbOutlined style={{ marginRight: 4 }} />
                            {reasonText}
                          </Text>
                        </div>
                      )}
                      <div style={{ marginTop: 8 }}>
                        <Button
                          size="small"
                          type="link"
                          onClick={() => {
                            if (recState.open) {
                              setRecProductsByKey((prev) => ({
                                ...prev,
                                [recKey]: { ...(prev[recKey] || {}), open: false },
                              }));
                            } else {
                              loadRecommendationProducts(recKey, fieldKey, group.isStandardField, uniqueCurrent);
                            }
                          }}
                        >
                          {recState.open ? 'Hide product details' : 'View product details'}
                        </Button>
                        {recState.open && productList.length > 0 && (
                          <Space size={6} wrap style={{ marginLeft: 8 }}>
                            <Button
                              size="small"
                              type="primary"
                              loading={!!reviewBulkLoading[`${recKey}_approve`]}
                              onClick={() => {
                                const effectiveGroupVal = recGroupOverrides[groupItem.ids[0]] ?? groupItem.suggestedValue;
                                const hasOverride = recGroupOverrides[groupItem.ids[0]] !== undefined && JSON.stringify(recGroupOverrides[groupItem.ids[0]]) !== JSON.stringify(groupItem.suggestedValue);
                                if (hasOverride) {
                                  applyBulkCustomValue({
                                    recKey,
                                    fieldKey,
                                    isStandardField: group.isStandardField,
                                    oldValues: uniqueCurrent,
                                    newValue: effectiveGroupVal,
                                    refreshCtx: {
                                      fieldKey,
                                      isStandardField: group.isStandardField,
                                      currentValues: uniqueCurrent,
                                    },
                                  });
                                } else {
                                  submitBulkStandardizeReviewDecision({
                                    recKey,
                                    products: productList,
                                    decision: 'approve',
                                    fields: [group.isStandardField ? fieldKey : 'attributes'],
                                    refreshRecommendationContext: {
                                      fieldKey,
                                      isStandardField: group.isStandardField,
                                      currentValues: uniqueCurrent,
                                    },
                                  });
                                }
                              }}
                            >
                              {recGroupOverrides[groupItem.ids[0]] !== undefined && JSON.stringify(recGroupOverrides[groupItem.ids[0]]) !== JSON.stringify(groupItem.suggestedValue)
                                ? 'Apply Edited Value to All'
                                : 'Approve All Loaded'}
                            </Button>
                            <Button
                              size="small"
                              danger
                              loading={!!reviewBulkLoading[`${recKey}_reject`]}
                              onClick={() =>
                                submitBulkStandardizeReviewDecision({
                                  recKey,
                                  products: productList,
                                  decision: 'reject',
                                  refreshRecommendationContext: {
                                    fieldKey,
                                    isStandardField: group.isStandardField,
                                    currentValues: uniqueCurrent,
                                  },
                                })
                              }
                            >
                              Reject All Loaded
                            </Button>
                          </Space>
                        )}
                      </div>
                      {recState.open && (
                        <div className="mt-2">
                          {recState.loading && <Text type="secondary">Loading products...</Text>}
                          {recState.error && <Alert type="error" message={recState.error} showIcon />}
                          {!recState.loading && !recState.error && productList.length > 0 && (
                            <div className="table-responsive">
                              <table className="table table-sm table-hover mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: '20%' }}>Product</th>
                                    <th style={{ width: '35%' }}>Input Value</th>
                                    <th style={{ width: '45%' }}>AI Recommendation Review</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {productList.map((prod) => (
                                    <tr key={prod.product_code}>
                                      <td className="align-middle">
                                        <div className="fw-semibold">{prod.product_code}</div>
                                        <div className="text-muted small">{prod.name || '—'}</div>
                                      </td>
                                      <td className="align-middle">
                                        {(() => {
                                          const inputValue = getFieldValueForReview(
                                            prod,
                                            fieldKey,
                                            group.isStandardField,
                                            'input'
                                          );
                                          return (
                                            <div className="small">
                                              <div>
                                                <strong>Input:</strong>{' '}
                                                {inputValue !== '' && inputValue !== null
                                                  ? String(inputValue)
                                                  : '—'}
                                              </div>
                                              <div className="text-muted mt-1">
                                                <strong>Category:</strong> {prod.category || '—'}
                                              </div>
                                            </div>
                                          );
                                        })()}
                                      </td>
                                      <td className="align-middle">
                                        {(() => {
                                          const aiValue = getFieldValueForReview(
                                            prod,
                                            fieldKey,
                                            group.isStandardField,
                                            'ai'
                                          );
                                          const loadingApprove = !!reviewActionLoading[`${prod.product_code}_approve`];
                                          const loadingReject = !!reviewActionLoading[`${prod.product_code}_reject`];
                                          const prodOverrideKey = `${recKey}__${prod.product_code}`;
                                          const prodOverride = recProductOverrides[prodOverrideKey];
                                          const aiRaw = aiValue ?? '';
                                          const inputDisplayVal = prodOverride !== undefined ? prodOverride : aiRaw;
                                          const isProdOverridden = prodOverride !== undefined && JSON.stringify(prodOverride) !== JSON.stringify(aiRaw);
                                          const normalizedFk = fieldKey.startsWith('attr_') ? fieldKey.slice(5) : fieldKey;
                                          const fieldParam = group.isStandardField ? normalizedFk : `attributes.${normalizedFk}`;
                                          return (
                                            <div className="small">
                                              <div>
                                                <strong>Value to apply:</strong>
                                                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                  {group.isStandardField ? (
                                                    <Input
                                                      size="small"
                                                      value={typeof inputDisplayVal === 'string' ? inputDisplayVal : String(inputDisplayVal)}
                                                      onChange={(e) =>
                                                        setRecProductOverrides((prev) => ({
                                                          ...prev,
                                                          [prodOverrideKey]: e.target.value,
                                                        }))
                                                      }
                                                      placeholder="Enter value…"
                                                      style={{ maxWidth: 180, borderColor: isProdOverridden ? '#fa8c16' : undefined }}
                                                    />
                                                  ) : (
                                                    <RecValueEditor
                                                      value={inputDisplayVal}
                                                      isAttribute={true}
                                                      overridden={isProdOverridden}
                                                      onChange={(v) =>
                                                        setRecProductOverrides((prev) => ({
                                                          ...prev,
                                                          [prodOverrideKey]: v,
                                                        }))
                                                      }
                                                    />
                                                  )}
                                                  {isProdOverridden && (
                                                    <Tooltip title="Reset to AI suggestion">
                                                      <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<RollbackOutlined />}
                                                        onClick={() =>
                                                          setRecProductOverrides((prev) => {
                                                            const n = { ...prev };
                                                            delete n[prodOverrideKey];
                                                            return n;
                                                          })
                                                        }
                                                        style={{ padding: 0, color: '#8c8c8c' }}
                                                      />
                                                    </Tooltip>
                                                  )}
                                                </div>
                                              </div>
                                              <div style={{ marginTop: 8 }}>
                                                <Space size={6} wrap>
                                                  <Button
                                                    size="small"
                                                    type="primary"
                                                    onClick={() => {
                                                      const oldValue = getFieldValueForReview(prod, fieldKey, group.isStandardField, 'input');
                                                      submitApplyChange({
                                                        productCode: prod.product_code,
                                                        field: fieldParam,
                                                        oldValue: String(oldValue ?? ''),
                                                        newValue: inputDisplayVal,
                                                        refreshCtx: {
                                                          recKey,
                                                          fieldKey,
                                                          isStandardField: group.isStandardField,
                                                          currentValues: uniqueCurrent,
                                                        },
                                                      });
                                                    }}
                                                    loading={loadingApprove}
                                                    disabled={Array.isArray(inputDisplayVal) ? inputDisplayVal.length === 0 : !inputDisplayVal}
                                                  >
                                                    {isProdOverridden ? 'Apply Edited' : 'Apply'}
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    danger
                                                    onClick={() =>
                                                      submitStandardizeReviewDecision({
                                                        productCode: prod.product_code,
                                                        decision: 'reject',
                                                        refreshRecommendationContext: {
                                                          recKey,
                                                          fieldKey,
                                                          isStandardField: group.isStandardField,
                                                          currentValues: uniqueCurrent,
                                                        },
                                                      })
                                                    }
                                                    loading={loadingReject}
                                                  >
                                                    Reject
                                                  </Button>
                                                </Space>
                                              </div>
                                            </div>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {!recState.loading && !recState.error && productList.length === 0 && (
                            <Text type="secondary">No products matched for these values.</Text>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                );
              });

              const noChangeValues = Array.from(new Set(noChange.flatMap((item) => item.currentValues || [])));
              const noChangeCount = noChange.reduce((sum, item) => sum + (item.existingCount || 0), 0);

              return (
                <>
                  {noChangeValues.length > 0 && (
                    <Card
                      size="small"
                      className="mb-3"
                      bodyStyle={{ padding: 12, background: '#f6ffed' }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <Space>
                          <Tag color="green">No change needed</Tag>
                          <Text type="secondary">{noChangeValues.length} values</Text>
                        </Space>
                        {noChangeCount > 0 && (
                          <Tag color="green">
                            <CheckCircleOutlined style={{ marginRight: 4 }} />
                            {noChangeCount} products already correct
                          </Tag>
                        )}
                      </div>
                      <div style={{ marginTop: 8 }} className="d-flex gap-2 flex-wrap">
                        {noChangeValues.map((v) => (
                          <Tag key={v} color="blue">
                            {v}
                          </Tag>
                        ))}
                      </div>
                    </Card>
                  )}
                  {changeCards}
                </>
              );
            })()}
          </Card>
        ))}
      </div>
    );
  };

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <BarChartOutlined />
          Overview
        </span>
      ),
      children: (
        <>
          <Card className="mb-4 shadow-sm" title="AI Analysis Overview">
            {analysisInsights.totalFields === 0 ? (
              <Empty
                description="No fields loaded yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Fields Analyzed"
                        value={analysisInsights.analyzedCount}
                        suffix={`/ ${analysisInsights.totalFields}`}
                        prefix={<CheckCircleOutlined />}
                      />
                      <Progress
                        percent={analysisInsights.analyzedPercent}
                        strokeColor="#52c41a"
                        size="small"
                        style={{ marginTop: 8 }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Fields Pending"
                        value={analysisInsights.pendingCount}
                        prefix={<WarningOutlined />}
                      />
                      <Progress
                        percent={analysisInsights.analyzedPercent}
                        strokeColor="#faad14"
                        trailColor="#fff1b8"
                        size="small"
                        showInfo={false}
                        style={{ marginTop: 8 }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Fields Need Review"
                        value={analysisInsights.needsReviewCount}
                        prefix={<InfoCircleOutlined />}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Recommendations saved for review
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Fields Need Action"
                        value={analysisInsights.needsActionCount}
                        prefix={<ExclamationCircleOutlined />}
                      />
                      <Progress
                        percent={analysisInsights.actionPercent}
                        strokeColor="#f5222d"
                        size="small"
                        style={{ marginTop: 8 }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Fields with AI Instructions"
                        value={analysisInsights.fieldsWithInstructionsCount}
                        prefix={<BulbOutlined style={{ color: '#faad14' }} />}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Field-level prompts configured
                      </Text>
                    </Card>
                  </Col>
                </Row>
                <Row gutter={[16, 16]} className="mt-3">
                  <Col xs={24} lg={12}>
                    <Card size="small" title="Uncategorized Values">
                      <Statistic
                        title="Fields with Uncategorised Values"
                        value={analysisInsights.uncategorizedFieldsCount}
                        suffix={
                          analysisInsights.totalUncategorizedValues
                            ? `/ ${analysisInsights.totalUncategorizedValues} values`
                            : ''
                        }
                      />
                      <Progress
                        percent={
                          analysisInsights.totalFields
                            ? Math.min(
                                100,
                                Math.round(
                                  (analysisInsights.uncategorizedFieldsCount /
                                    analysisInsights.totalFields) *
                                    100
                                )
                              )
                            : 0
                        }
                        strokeColor="#faad14"
                        size="small"
                        style={{ marginTop: 8 }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card size="small" title="Recommendation Coverage">
                      <div className="d-flex flex-column gap-2">
                        <div>
                          <Text type="secondary">Fields with recommendations</Text>
                          <Progress
                            percent={
                              analysisInsights.totalFields
                                ? Math.min(
                                    100,
                                    Math.round(
                                      (analysisInsights.needsReviewCount /
                                        analysisInsights.totalFields) *
                                        100
                                    )
                                  )
                                : 0
                            }
                            strokeColor="#1890ff"
                            size="small"
                          />
                        </div>
                        <div>
                          <Text type="secondary">Fields needing action</Text>
                          <Progress
                            percent={analysisInsights.actionPercent}
                            strokeColor="#f5222d"
                            size="small"
                          />
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Card>

          {summary && (
            <>
              <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} md={6}>
                  <Card className="shadow-sm h-100">
                    <Statistic
                      title="Total Products"
                      value={summary.total_products}
                      prefix={<DatabaseOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card className="shadow-sm h-100">
                    <Statistic
                      title="Quality Score"
                      value={summary.quality_score}
                      suffix="%"
                      valueStyle={{ color: getScoreColor(summary.quality_score) }}
                      prefix={
                        summary.quality_score >= 80 ? (
                          <CheckCircleOutlined />
                        ) : summary.quality_score >= 60 ? (
                          <WarningOutlined />
                        ) : (
                          <ExclamationCircleOutlined />
                        )
                      }
                    />
                    <Progress
                      percent={summary.quality_score}
                      showInfo={false}
                      strokeColor={getScoreColor(summary.quality_score)}
                      size="small"
                      style={{ marginTop: 8 }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card className="shadow-sm h-100">
                    <Statistic
                      title="Products with Attributes"
                      value={summary.products_with_attributes}
                      suffix={`/ ${summary.total_products}`}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {summary.attribute_coverage_percent}% coverage
                    </Text>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card className="shadow-sm h-100">
                    <Statistic
                      title="Total Issues"
                      value={summary.issues_summary?.total_issues || 0}
                      valueStyle={{
                        color:
                          summary.issues_summary?.critical_issues > 0 ? '#f5222d' : '#faad14',
                      }}
                    />
                    <Space style={{ marginTop: 8 }}>
                      <Tag color="red">{summary.issues_summary?.critical_issues || 0} Critical</Tag>
                      <Tag color="orange">
                        {summary.issues_summary?.warning_issues || 0} Warning
                      </Tag>
                    </Space>
                  </Card>
                </Col>
              </Row>

              {summary.missing_data_issues && summary.missing_data_issues.length > 0 && (
                <Card
                  title={
                    <span>
                      <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                      Data Quality Issues
                    </span>
                  }
                  className="mb-4 shadow-sm"
                >
                  <List
                    dataSource={summary.missing_data_issues}
                    renderItem={(issue) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={getSeverityTag(issue.severity)}
                          title={issue.message}
                          description={`Field: ${issue.field} | Affected: ${issue.count} products`}
                        />
                      </List.Item>
                    )}
                  />
                </Card>
              )}

              {summary.recommendations && summary.recommendations.length > 0 && (
                <Card
                  title={
                    <span>
                      <BulbOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                      Recommendations
                    </span>
                  }
                  className="shadow-sm"
                >
                  <List
                    dataSource={summary.recommendations}
                    renderItem={(rec) => (
                      <List.Item>
                        <Text>{rec}</Text>
                      </List.Item>
                    )}
                  />
                </Card>
              )}

              <Card className="shadow-sm mt-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      AI Standardize Products
                    </Title>
                    <Text type="secondary">
                      Enhance product data for easier search. Paste product codes to standardize.
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    icon={<RobotOutlined />}
                    onClick={() => openStandardizeModal(summaryStandardizeCodes)}
                    disabled={summaryStandardizeCodes.length === 0}
                  >
                    Configure & Run
                  </Button>
                </div>
                <TextArea
                  rows={3}
                  value={summaryStandardizeCodes.join('\n')}
                  onChange={(e) => updateSummaryCodes(e.target.value)}
                  placeholder="Enter product codes separated by commas or new lines"
                />
              </Card>
            </>
          )}
        </>
      ),
    },
    {
      key: 'ai-analysis',
      label: (
        <span>
          <RobotOutlined />
          AI Analysis
          {selectedFields.length > 0 && (
            <Badge count={selectedFields.length} style={{ marginLeft: 8 }} size="small" />
          )}
        </span>
      ),
      children: (
        <div>
          {/* Steps indicator */}
          <Card className="mb-4 shadow-sm">
            <div className="d-flex justify-content-between align-items-center">
              <Steps
                current={analysisStep}
                style={{ flex: 1 }}
                onChange={(step) => {
                  // Allow going back to previous steps
                  if (step < analysisStep) {
                    setAnalysisStep(step);
                  }
                }}
                items={[
                  {
                    title: 'Select Fields',
                    icon: <CheckSquareOutlined />,
                    style: { cursor: analysisStep > 0 ? 'pointer' : 'default' },
                  },
                  {
                    title: 'Run Analysis',
                    icon: analysisStep === 1 ? <Spin size="small" /> : <RobotOutlined />,
                  },
                  {
                    title: 'Review & Apply',
                    icon: <FileTextOutlined />,
                  },
                ]}
              />
              {analysisStep > 0 && (
                <Space style={{ marginLeft: 16 }}>
                  <Button
                    icon={<RollbackOutlined />}
                    onClick={exitToSelection}
                  >
                    Back to Selection
                  </Button>
                  <Tooltip title="Clear all results and start over">
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={resetAnalysis}
                    >
                      Reset
                    </Button>
                  </Tooltip>
                </Space>
              )}
            </div>
          </Card>

          {/* Step 0: Select Fields */}
          {analysisStep === 0 && (
            <Card className="shadow-sm">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    Select Fields to Analyze
                  </Title>
                  <Text type="secondary">
                    Choose standard fields and/or attributes for AI quality analysis
                  </Text>
                </div>
                <Space wrap>
                  <Search
                    placeholder="Search fields..."
                    value={fieldSearch}
                    onChange={(e) => setFieldSearch(e.target.value)}
                    style={{ width: 200 }}
                    allowClear
                  />
                  <Checkbox
                    checked={showStandardFields}
                    onChange={(e) => setShowStandardFields(e.target.checked)}
                  >
                    Standard Fields
                  </Checkbox>
                  <Checkbox
                    checked={showAttributeFields}
                    onChange={(e) => setShowAttributeFields(e.target.checked)}
                  >
                    Attributes
                  </Checkbox>
                </Space>
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button onClick={selectAllFields} icon={<CheckSquareOutlined />}>
                  Select All ({combinedFields.length})
                </Button>
                <Button onClick={clearSelection} disabled={selectedFields.length === 0}>
                  Clear Selection
                </Button>
                <Divider type="vertical" />
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={runBatchAnalysis}
                  disabled={selectedFields.length === 0}
                >
                  Analyze Selected ({selectedFields.length})
                </Button>
              </div>

              <Alert
                message="Tip: Click the gear icon on any field to add custom AI instructions or specify golden (approved) values"
                type="info"
                showIcon
                className="mb-3"
              />

              <Row gutter={[12, 12]}>{combinedFields.map(renderFieldCard)}</Row>

              {combinedFields.length === 0 && <Empty description="No fields found" />}
            </Card>
          )}

          {/* Step 1: Running Analysis */}
          {analysisStep === 1 && (
            <Card className="shadow-sm text-center py-5">
              <Spin size="large" />
              <Title level={4} style={{ marginTop: 24 }}>
                Analyzing Fields...
              </Title>
              <Progress
                percent={Math.round((batchProgress.current / batchProgress.total) * 100)}
                status="active"
                style={{ maxWidth: 400, margin: '24px auto' }}
              />
              <Text type="secondary">
                Analyzing {batchProgress.currentField} ({batchProgress.current} of{' '}
                {batchProgress.total})
              </Text>
              <div style={{ marginTop: 24 }}>
                <Space>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={cancelAnalysisJob}
                    disabled={!analysisJobStatus?.status || analysisJobStatus?.status !== 'running'}
                  >
                    Stop AI Analysis
                  </Button>
                  <Button icon={<CloseCircleOutlined />} onClick={exitToSelection}>
                    Cancel & Keep Results
                  </Button>
                  <Button danger icon={<CloseCircleOutlined />} onClick={resetAnalysis}>
                    Cancel & Clear All
                  </Button>
                </Space>
              </div>
            </Card>
          )}

          {/* Step 2: Results */}
          {analysisStep === 2 && (
            <div>
              <Card className="mb-4 shadow-sm">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Analysis Complete
                    </Title>
                    <Text type="secondary">
                      {selectedFields.length} fields analyzed, {recommendations.length}{' '}
                      recommendations
                    </Text>
                  </div>
                  <Space>
                    <Tooltip
                      title={
                        fieldsWithUncategorized.length
                          ? `Complete ${totalUncategorizedValues} uncategorized values across ${fieldsWithUncategorized.length} fields`
                          : 'No uncategorized values remaining'
                      }
                    >
                      <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        onClick={runFullBatchForUncategorizedFields}
                        disabled={fieldsWithUncategorized.length === 0}
                      >
                        Complete Uncategorized ({fieldsWithUncategorized.length})
                      </Button>
                    </Tooltip>
                    <Button onClick={() => setAnalysisStep(0)} icon={<EditOutlined />}>
                      Analyze More
                    </Button>
                    <Button
                      type="primary"
                      icon={<BulbOutlined />}
                      onClick={() => setActiveTab('recommendations')}
                      disabled={recommendations.length === 0}
                    >
                      View Recommendations ({recommendations.length})
                    </Button>
                  </Space>
                </div>
              </Card>

              {renderAnalysisResults()}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'batch-jobs',
      label: (
        <span>
          <ThunderboltOutlined />
          Batch Jobs
          {batchJobs.length > 0 && (
            <Badge count={batchJobs.length} style={{ marginLeft: 8 }} size="small" />
          )}
        </span>
      ),
      children: (
        <Card className="shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Title level={5} style={{ margin: 0 }}>
              Batch Jobs
            </Title>
            <Space>
              <Button danger icon={<CloseCircleOutlined />} onClick={stopAllBatchJobs}>
                Stop All
              </Button>
            </Space>
          </div>
          {analysisJobStatus && (
            <Card size="small" className="mb-3" title="AI Analysis Job">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <Text strong>Status: </Text>
                  <Tag color={analysisJobStatus.status === 'running' ? 'blue' : analysisJobStatus.status === 'completed' ? 'green' : 'orange'}>
                    {analysisJobStatus.status}
                  </Tag>
                </div>
                <Button
                  danger
                  size="small"
                  onClick={cancelAnalysisJob}
                  disabled={analysisJobStatus.status !== 'running'}
                >
                  Stop
                </Button>
              </div>
              <Progress
                percent={
                  analysisJobStatus.total
                    ? Math.round((analysisJobStatus.current / analysisJobStatus.total) * 100)
                    : 0
                }
                size="small"
                status={analysisJobStatus.status === 'running' ? 'active' : 'normal'}
                style={{ marginTop: 12 }}
              />
            </Card>
          )}
          {batchJobs.length === 0 ? (
            <Empty description="No batch jobs running or completed yet" />
          ) : (
            <Table
              dataSource={batchJobs}
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Field',
                  dataIndex: 'fieldLabel',
                  key: 'field',
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status) => {
                    const color =
                      status === 'Running' ? 'blue' : status === 'Cancelled' ? 'red' : status === 'Completed' ? 'green' : 'default';
                    return <Tag color={color}>{status}</Tag>;
                  },
                },
                {
                  title: 'Progress',
                  key: 'progress',
                  render: (_, record) => (
                    <Space>
                      <Progress
                        percent={record.progress}
                        size="small"
                        status={record.status === 'Cancelled' ? 'exception' : 'active'}
                        style={{ width: 140 }}
                      />
                      <Text type="secondary">
                        {record.currentBatch}/{record.totalBatches || '—'}
                      </Text>
                    </Space>
                  ),
                },
                {
                  title: 'Remaining',
                  dataIndex: 'remaining',
                  key: 'remaining',
                  render: (val) => (val === undefined || val === null ? '—' : val),
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, record) =>
                    record.running ? (
                      <Button
                        size="small"
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => cancelBatchAnalysis(record.key)}
                      >
                        Stop
                      </Button>
                    ) : (
                      <Text type="secondary">—</Text>
                    ),
                },
              ]}
            />
          )}
        </Card>
      ),
    },
    {
      key: 'recommendations',
      label: (
        <span>
          <BulbOutlined />
          Recommendations
          {recommendations.length > 0 && (
            <Badge count={recommendations.length} style={{ marginLeft: 8 }} size="small" />
          )}
        </span>
      ),
      children: (
        <Card className="shadow-sm">
          <div className="mb-4">
            <Title level={5} style={{ margin: 0 }}>
              AI Recommendations
            </Title>
            <Text type="secondary">Review and apply suggested data standardizations</Text>
          </div>
          {renderRecommendations()}
        </Card>
      ),
    },
    {
      key: 'issues',
      label: (
        <span>
          <WarningOutlined />
          Products with Issues
          {summary?.issues_summary?.total_issues > 0 && (
            <Badge
              count={summary.issues_summary.total_issues}
              style={{ marginLeft: 8 }}
              size="small"
            />
          )}
        </span>
      ),
      children: (
        <Card className="shadow-sm">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
            <Title level={5} style={{ margin: 0 }}>
              Products with Data Quality Issues
            </Title>
            <Space>
              <Button
                type="primary"
                icon={<RobotOutlined />}
                disabled={selectedIssueRows.length === 0}
                onClick={() =>
                  openStandardizeModal(
                    selectedIssueRows.map((row) => row.product_code).filter(Boolean)
                  )
                }
              >
                AI Standardize Selected {selectedIssueRows.length ? `(${selectedIssueRows.length})` : ''}
              </Button>
              <Search
                allowClear
                placeholder="Search code, name, category, manufacturer"
                style={{ width: 320 }}
                value={issuesSearch}
                onChange={(e) => setIssuesSearch(e.target.value)}
              />
              <Select
                value={issueTypeFilter}
                style={{ width: 180 }}
                onChange={(v) => {
                  setIssueTypeFilter(v);
                  setIssuesPage(1);
                }}
              >
                <Option value="all">All Issues</Option>
                <Option value="missing_name">Missing Name</Option>
                <Option value="missing_category">Missing Category</Option>
                <Option value="missing_manufacturer">Missing Manufacturer</Option>
                <Option value="no_attributes">No Attributes</Option>
              </Select>
              <Button
                icon={<SyncOutlined />}
                onClick={() =>
                  fetchProductsWithIssues({
                    issueType: issueTypeFilter,
                    search: issuesSearch,
                    page: issuesPage,
                    pageSize: issuesPageSize,
                  })
                }
                loading={issuesLoading}
              >
                Refresh
              </Button>
            </Space>
          </div>
          <div className="mb-2">
            <Text type="secondary">
              Showing {productsWithIssues.length} of {issuesTotalCount} products
            </Text>
          </div>
          <Table
            dataSource={productsWithIssues}
            columns={issuesColumns}
            rowKey="id"
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: selectedIssueKeys,
              onChange: (keys, rows) => {
                setSelectedIssueKeys(keys);
                setSelectedIssueRows(rows || []);
              },
            }}
            rowSelectionColumnWidth={48}
            onRow={(record) => ({
              onClick: () => {
                const key = record.id;
                if (selectedIssueKeys.includes(key)) {
                  const nextKeys = selectedIssueKeys.filter((k) => k !== key);
                  setSelectedIssueKeys(nextKeys);
                  setSelectedIssueRows((prev) => prev.filter((row) => row.id !== key));
                } else {
                  setSelectedIssueKeys((prev) => [...prev, key]);
                  setSelectedIssueRows((prev) => [...prev, record]);
                }
              },
            })}
            pagination={{
              current: issuesPage,
              pageSize: issuesPageSize,
              total: issuesTotalCount,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: (page, pageSize) => {
                setIssuesPage(page);
                setIssuesPageSize(pageSize);
                fetchProductsWithIssues({
                  issueType: issueTypeFilter,
                  search: issuesSearch,
                  page,
                  pageSize,
                });
              },
            }}
            size="small"
            loading={issuesLoading}
            locale={{
              emptyText: <Empty description="No products with issues found" />,
            }}
          />
        </Card>
      ),
    },
    {
      key: 'ambiguous-mappings',
      label: (
        <span>
          <ApartmentOutlined />
          Ambiguous Mappings
        </span>
      ),
      children: (
        <Card className="shadow-sm">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
            <Title level={5} style={{ margin: 0 }}>
              Alt / Supplier Part Numbers Mapped to Multiple Products
            </Title>
            <Space wrap>
              <Select
                value={ambiguousMode}
                style={{ width: 210 }}
                onChange={(v) => {
                  setAmbiguousMode(v);
                  setAmbiguousPage(1);
                  setAmbiguousSearch('');
                }}
              >
                <Option value="alt">Alt Part Numbers</Option>
                <Option value="supplier">Supplier Part Numbers</Option>
              </Select>
              <Search
                allowClear
                placeholder={ambiguousMode === 'supplier' ? 'Search supplier or part number' : 'Search alt part number'}
                style={{ width: 280 }}
                value={ambiguousSearch}
                onChange={(e) => setAmbiguousSearch(e.target.value)}
              />
              <Button
                icon={<SyncOutlined />}
                onClick={() =>
                  fetchAmbiguousItems({
                    mode: ambiguousMode,
                    search: ambiguousSearch,
                    page: ambiguousPage,
                    pageSize: ambiguousPageSize,
                  })
                }
                loading={ambiguousLoading}
              >
                Refresh
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportAmbiguousItems('xlsx')}
                loading={ambiguousExporting}
              >
                Export to Excel
              </Button>
            </Space>
          </div>
          <div className="mb-2">
            <Text type="secondary">
              {ambiguousTotalCount} ambiguous {ambiguousMode === 'supplier' ? 'supplier part number(s)' : 'alt part number(s)'} found
              -- each maps to more than one distinct product. Blank values and the literal placeholder
              "NULL" are excluded.
            </Text>
          </div>
          <Table
            dataSource={ambiguousItems}
            columns={ambiguousColumns}
            rowKey={(record) =>
              ambiguousMode === 'supplier'
                ? `${record.supplier_name || ''}::${record.supplier_part_number || ''}`
                : record.alt_part_number
            }
            pagination={{
              current: ambiguousPage,
              pageSize: ambiguousPageSize,
              total: ambiguousTotalCount,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: (page, pageSize) => {
                setAmbiguousPage(page);
                setAmbiguousPageSize(pageSize);
                fetchAmbiguousItems({
                  mode: ambiguousMode,
                  search: ambiguousSearch,
                  page,
                  pageSize,
                });
              },
            }}
            size="small"
            loading={ambiguousLoading}
            locale={{
              emptyText: <Empty description="No ambiguous mappings found" />,
            }}
          />
        </Card>
      ),
    },
  ];

  return (
    <div className="data-quality-page" style={{ padding: '0 8px' }}>
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <DatabaseOutlined style={{ color: '#1890ff' }} />
            Data Quality
          </Title>
          <Text type="secondary">AI-powered product data analysis and improvement</Text>
        </div>
        <Space>
          <Tooltip title="AI Engine Settings">
            <Button
              icon={<RobotOutlined />}
              onClick={() => setAiSettingsModalVisible(true)}
            >
              AI Engine
            </Button>
          </Tooltip>
          <Tooltip title="Default AI prompt for data quality">
            <Button
              icon={<SettingOutlined />}
              onClick={() => setPromptModalVisible(true)}
            />
          </Tooltip>
          <Button
            icon={<SyncOutlined />}
            onClick={() => {
              fetchSummary();
              fetchFields();
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Quality Score Banner */}
      {summary && summary.quality_score < 80 && (
        <Alert
          message={
            <span>
              Data Quality Score: <strong>{summary.quality_score}%</strong>
            </span>
          }
          description={`${summary.issues_summary?.total_issues || 0} issues found. Run AI analysis for detailed suggestions.`}
          type={summary.quality_score < 60 ? 'error' : 'warning'}
          showIcon
          className="mb-4"
          action={
            <Button size="small" onClick={() => setActiveTab('ai-analysis')}>
              Run AI Analysis
            </Button>
          }
        />
      )}

      {loading && !summary ? (
        <div className="text-center py-5">
          <Spin size="large" />
          <div className="mt-3">
            <Text type="secondary">Loading data quality metrics...</Text>
          </div>
        </div>
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      )}

      {/* Field Configuration Drawer */}
      <Drawer
        title={
          <Space>
            <SettingOutlined />
            Configure: {configField?.label || configField?.name}
          </Space>
        }
        placement="right"
        width={480}
        open={configDrawerVisible}
        onClose={() => setConfigDrawerVisible(false)}
      >
        {configField && (
          <Form
            key={configField.key}
            form={configForm}
            layout="vertical"
            initialValues={{
              custom_prompt: fieldConfigs[configField.key]?.custom_prompt || '',
              golden_values: (fieldConfigs[configField.key]?.golden_values || []).join('\n'),
              additional_fields: (fieldConfigs[configField.key]?.additional_fields || []).filter(
                (key) => key !== configField.key
              ),
            }}
            onFinish={saveFieldConfig}
          >
            <Alert
              message="AI Configuration"
              description="Customize how the AI analyzes this field. Add instructions, select extra product fields for AI context, and provide approved values."
              type="info"
              showIcon
              className="mb-4"
            />

            <Form.Item
              name="custom_prompt"
              label={
                <Space>
                  <RobotOutlined />
                  Custom AI Instructions
                </Space>
              }
              help="Additional instructions for the AI when analyzing this field"
            >
              <TextArea
                rows={4}
                placeholder="e.g., 'Use title case for all values', 'Prefer abbreviations', 'Merge similar brand names'..."
              />
            </Form.Item>

            <Divider />

            <Form.Item
              name="additional_fields"
              label={
                <Space>
                  <DatabaseOutlined />
                  Additional Context Fields for AI
                </Space>
              }
              help="Select extra fields to send to AI as context (for example, include name and description when standardizing category)."
            >
              <Select
                mode="multiple"
                placeholder="Select additional fields..."
                options={additionalFieldOptions.filter((opt) => opt.value !== configField.key)}
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
              />
            </Form.Item>
            <div style={{ marginTop: -8, marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Selected Context Fields ({watchedAdditionalFields.length})
              </Text>
              <div style={{ marginTop: 6 }}>
                {watchedAdditionalFields.length > 0 ? (
                  <Space size={[4, 4]} wrap>
                    {watchedAdditionalFields.map((fieldKey) => (
                      <Tag key={fieldKey} color="blue">
                        {fieldLabelByKey[fieldKey] || fieldKey}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    None selected
                  </Text>
                )}
              </div>
            </div>

            <Form.Item
              name="golden_values"
              label={
                <Space>
                  <StarOutlined style={{ color: '#faad14' }} />
                  Golden Values (Approved Reference Values)
                </Space>
              }
              help="Enter one value per line. The AI will try to map existing values to these approved values."
            >
              <TextArea
                rows={8}
                placeholder={`e.g.:\nGoodman\nDaikin\nCarrier\nTrane\nLennox`}
              />
            </Form.Item>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                Field: {configField.name}
                <br />
                Type: {configField.fieldType === 'standard' ? 'Standard Field' : 'Attribute'}
                <br />
                Unique Values: {configField.unique_values || 0}
              </Text>
            </div>

            <Divider />

            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                Save Configuration
              </Button>
              <Button onClick={() => setConfigDrawerVisible(false)}>Cancel</Button>
            </Space>
          </Form>
        )}
      </Drawer>

      <Drawer
        title={
          <Space>
            <RobotOutlined />
            AI Standardize Products
          </Space>
        }
        placement="right"
        width={Math.min(
          Math.round(viewportWidth * 0.75),
          Math.round((viewportWidth * standardizeDrawerWidthPct) / 100)
        )}
        open={standardizeModalVisible}
        onClose={() => setStandardizeModalVisible(false)}
      >
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <Text type="secondary">Resize editor (25% - 75%)</Text>
          <div style={{ minWidth: 180 }}>
            <input
              type="range"
              min={25}
              max={75}
              value={standardizeDrawerWidthPct}
              onChange={(e) => setStandardizeDrawerWidthPct(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
          <Tag>{standardizeDrawerWidthPct}%</Tag>
        </div>

        <Alert
          message="Enhance and standardize selected products"
          description="Runs AI standardization using all product fields and attributes, plus any saved field-level AI instructions and golden values."
          type="info"
          showIcon
          className="mb-3"
        />

        <Form layout="vertical">
          <Form.Item
            label="Product Codes"
            extra={`${getMergedStandardizeCodes().length} selected`}
          >
            <TextArea
              rows={4}
              value={getMergedStandardizeCodes().join('\n')}
              onChange={(e) => updateStandardizeCodes(e.target.value)}
              placeholder="Enter product codes separated by commas or new lines"
            />
          </Form.Item>

          <Form.Item label="Select products with issues">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <Space>
                <Button
                  size="small"
                  onClick={() => fetchProductsWithIssues('all')}
                  loading={issuesLoading}
                >
                  Load Products with Issues
                </Button>
                <Button
                  size="small"
                  type="primary"
                  disabled={selectedIssueRows.length === 0}
                  onClick={() =>
                    appendStandardizeCodes(
                      selectedIssueRows.map((row) => row.product_code).filter(Boolean)
                    )
                  }
                >
                  Add Selected ({selectedIssueRows.length})
                </Button>
              </Space>
              {selectedIssueRows.length > 0 && (
                <Text type="secondary">Selected {selectedIssueRows.length} products</Text>
              )}
            </div>
            {productsWithIssues.length > 0 ? (
              <Table
                dataSource={productsWithIssues}
                columns={issuesColumns}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 5 }}
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedIssueKeys,
                  onChange: (keys, rows) => {
                    setSelectedIssueKeys(keys);
                    setSelectedIssueRows(rows || []);
                  },
                }}
                rowSelectionColumnWidth={48}
                onRow={(record) => ({
                  onClick: () => {
                    const key = record.id;
                    if (selectedIssueKeys.includes(key)) {
                      const nextKeys = selectedIssueKeys.filter((k) => k !== key);
                      setSelectedIssueKeys(nextKeys);
                      setSelectedIssueRows((prev) => prev.filter((row) => row.id !== key));
                    } else {
                      setSelectedIssueKeys((prev) => [...prev, key]);
                      setSelectedIssueRows((prev) => [...prev, record]);
                    }
                  },
                })}
              />
            ) : (
              <Text type="secondary">
                Load products with issues to select them for AI standardization.
              </Text>
            )}
          </Form.Item>

          <Form.Item label="AI Engine">
            <Select value={standardizeEngine} onChange={setStandardizeEngine}>
              <Option value="openai">OpenAI (GPT)</Option>
              <Option value="anthropic">Anthropic (Claude)</Option>
              <Option value="gemini">Google Gemini</Option>
            </Select>
          </Form.Item>

          <Form.Item label="System Prompt (optional)">
            <TextArea
              rows={4}
              value={standardizePrompt}
              onChange={(e) => setStandardizePrompt(e.target.value)}
              placeholder="Leave blank to use the default prompt"
            />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={standardizeDebugFirstRecord}
              onChange={(e) => setStandardizeDebugFirstRecord(!!e.target.checked)}
            >
              Debug first record (print AI request/response with header in backend logs)
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={standardizeExcludeOwnFieldValueSource}
              onChange={(e) => setStandardizeExcludeOwnFieldValueSource(!!e.target.checked)}
            >
              Exclude selected field current values from AI source context
            </Checkbox>
          </Form.Item>

          <Form.Item
            label="Fields to Standardize for Selected Products"
            extra="AI will standardize only these fields for this run."
          >
            <Select
              mode="multiple"
              value={standardizeTargetFields}
              onChange={(vals) => setStandardizeTargetFields(vals || [])}
              options={STANDARDIZE_TARGET_FIELD_OPTIONS}
              placeholder="Select fields to standardize"
              maxTagCount="responsive"
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Selected Fields ({standardizeTargetFields.length})
              </Text>
              <div style={{ marginTop: 6 }}>
                {standardizeTargetFields.length > 0 ? (
                  <Space size={[4, 4]} wrap>
                    {standardizeTargetFields.map((fieldKey) => (
                      <Tag key={fieldKey} color="blue">
                        {STANDARDIZE_TARGET_FIELD_OPTIONS.find((opt) => opt.value === fieldKey)?.label ||
                          fieldKey}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    None selected
                  </Text>
                )}
              </div>
            </div>
          </Form.Item>

          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={standardizeRunning}
              onClick={runAiStandardize}
              disabled={standardizeProductCodes.length === 0 || standardizeTargetFields.length === 0}
            >
              {standardizeRunning ? 'Running...' : 'Run Standardization'}
            </Button>
            <Button
              loading={standardizeDryRunRunning}
              onClick={runAiConsistencyDryRun}
              disabled={standardizeProductCodes.length === 0 || standardizeTargetFields.length === 0}
            >
              {standardizeDryRunRunning ? 'Dry-Run...' : 'Run Consistency Dry-Run'}
            </Button>
            <Button onClick={() => setStandardizeModalVisible(false)}>Close</Button>
          </Space>
        </Form>

        {standardizeResults.length > 0 && (
          <div className="mt-4">
            <Divider />
            <Title level={5} style={{ marginBottom: 12 }}>
              Results
            </Title>
            {standardizeConsistencyReport?.summary && (
              <Alert
                type="info"
                showIcon
                className="mb-2"
                message="Category consistency analysis"
                description={
                  <span>
                    Families: {standardizeConsistencyReport.summary.total_families || 0}
                    {' '}| Needs review: {standardizeConsistencyReport.summary.families_needing_review || 0}
                    {' '}| Would align: {standardizeConsistencyReport.summary.rows_would_align || 0}
                    {' '}| Auto-aligned: {standardizeConsistencyReport.summary.rows_auto_aligned || 0}
                  </span>
                }
              />
            )}
            <div className="mb-2">
              <Checkbox
                checked={standardizeNeedsReviewOnly}
                onChange={(e) => setStandardizeNeedsReviewOnly(!!e.target.checked)}
              >
                Show needs_review rows only
              </Checkbox>
            </div>
            {Array.isArray(standardizeConsistencyReport?.families) && standardizeConsistencyReport.families.length > 0 && (
              <List
                size="small"
                className="mb-2"
                dataSource={standardizeConsistencyReport.families.slice(0, 12)}
                renderItem={(family) => (
                  <List.Item>
                    <Space size={[6, 6]} wrap>
                      <Tag color="geekblue">{family.family_key || 'family'}</Tag>
                      <Tag color={family.status === 'needs_review' ? 'orange' : 'blue'}>
                        {family.status || 'unknown'}
                      </Tag>
                      <Tag>Size: {family.family_size || 0}</Tag>
                      {family.canonical_category && <Tag color="cyan">{family.canonical_category}</Tag>}
                    </Space>
                  </List.Item>
                )}
              />
            )}
            <List
              dataSource={visibleStandardizeResults}
              renderItem={(item) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <Space>
                      <Tag color={item.status === 'ok' ? 'green' : 'red'}>
                        {item.status === 'ok' ? 'Updated' : 'Error'}
                      </Tag>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => loadStandardizeDetail(item.product_code)}
                      >
                        {item.product_code}
                      </Button>
                      {item?.consistency?.family_key && (
                        <Tag color="geekblue">Family: {item.consistency.family_key}</Tag>
                      )}
                      {item?.consistency?.category_consistency_status && (
                        <Tag color={item.consistency.category_consistency_status === 'needs_review' ? 'orange' : 'blue'}>
                          {item.consistency.category_consistency_status}
                        </Tag>
                      )}
                      {item.error && <Text type="danger">{item.error}</Text>}
                    </Space>
                    {(item.ai_response || item.ai_repaired_response) && (
                      <Collapse size="small" className="mt-2" items={[
                        item.ai_response
                          ? {
                              key: 'raw',
                              label: 'Raw AI response',
                              children: (
                                <pre
                                  style={{
                                    maxHeight: 220,
                                    overflow: 'auto',
                                    background: '#f8f9fa',
                                    padding: 8,
                                    borderRadius: 6,
                                    fontSize: 11,
                                    marginBottom: 0,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {item.ai_response}
                                </pre>
                              ),
                            }
                          : null,
                        item.ai_repaired_response
                          ? {
                              key: 'repaired',
                              label: 'Repaired AI response',
                              children: (
                                <pre
                                  style={{
                                    maxHeight: 220,
                                    overflow: 'auto',
                                    background: '#f8f9fa',
                                    padding: 8,
                                    borderRadius: 6,
                                    fontSize: 11,
                                    marginBottom: 0,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {item.ai_repaired_response}
                                </pre>
                              ),
                            }
                          : null,
                      ].filter(Boolean)} />
                    )}
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}

        {(standardizeDetailLoading || standardizeDetailError || standardizeDetailProduct) && (
          <div className="mt-4">
            <Divider />
            <Title level={5} style={{ marginBottom: 12 }}>
              Product Details
            </Title>
            {standardizeDetailLoading && <Text type="secondary">Loading product...</Text>}
            {standardizeDetailError && (
              <Alert type="error" showIcon message={standardizeDetailError} />
            )}
            {standardizeDetailProduct && (
              <Card size="small">
                {(() => {
                  const aiPayload = parseJsonValue(standardizeDetailProduct.ai_standardized, {});
                  const hasAiData = aiPayload && typeof aiPayload === 'object' && Object.keys(aiPayload).length > 0;
                  const loadingApprove = !!reviewActionLoading[`${standardizeDetailProduct.product_code}_approve`];
                  const loadingReject = !!reviewActionLoading[`${standardizeDetailProduct.product_code}_reject`];
                  return (
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={12}>
                    <Text strong>Input (Current Record)</Text>
                    <div className="small text-muted">
                      {standardizeDetailProduct.product_code} • {standardizeDetailProduct.name || '—'}
                    </div>
                    <div className="small">
                      <div><strong>Name:</strong> {standardizeDetailProduct.name || '—'}</div>
                      <div><strong>Description:</strong> {standardizeDetailProduct.description || '—'}</div>
                      <div><strong>Manufacturer:</strong> {standardizeDetailProduct.manufacturer_name || '—'}</div>
                      <div><strong>MPN:</strong> {standardizeDetailProduct.manufacturer_part_number || '—'}</div>
                      <div><strong>Category:</strong> {standardizeDetailProduct.category || '—'}</div>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <Text strong>AI Recommendation</Text>
                    <pre
                      style={{
                        maxHeight: 220,
                        overflow: 'auto',
                        background: '#f8f9fa',
                        padding: 8,
                        borderRadius: 6,
                        fontSize: 11,
                      }}
                    >
                      {JSON.stringify(aiPayload, null, 2)}
                    </pre>
                    <Space size={8} wrap style={{ marginTop: 8 }}>
                      <Button
                        type="primary"
                        onClick={() =>
                          submitStandardizeReviewDecision({
                            productCode: standardizeDetailProduct.product_code,
                            decision: 'approve',
                          })
                        }
                        loading={loadingApprove}
                        disabled={!hasAiData}
                      >
                        Approve & Apply
                      </Button>
                      <Button
                        danger
                        onClick={() =>
                          submitStandardizeReviewDecision({
                            productCode: standardizeDetailProduct.product_code,
                            decision: 'reject',
                          })
                        }
                        loading={loadingReject}
                        disabled={!hasAiData}
                      >
                        Reject
                      </Button>
                    </Space>
                  </Col>
                </Row>
                  );
                })()}
              </Card>
            )}
          </div>
        )}
      </Drawer>

      <Modal
        open={promptModalVisible}
        onOk={async () => {
          setPromptLoading(true);
          await saveDataQualityPrompt();
          setPromptLoading(false);
          setPromptModalVisible(false);
        }}
        onCancel={() => setPromptModalVisible(false)}
        title={
          <Space>
            <SettingOutlined />
            Data Quality AI Prompt
          </Space>
        }
        okText="Close"
        okButtonProps={{ loading: promptLoading }}
      >
        <Text type="secondary">
          Edit the default AI prompt used for recommendations when a field does not have its own custom prompt.
        </Text>
        <TextArea
          rows={6}
          className="mt-2"
          value={dataQualityPrompt}
          onChange={(e) => setDataQualityPrompt(e.target.value)}
          placeholder="Enter the default AI prompt for data quality recommendations"
        />
      </Modal>

      {/* AI Engine Settings Modal */}
      <Modal
        open={aiSettingsModalVisible}
        onOk={async () => {
          setAiSettingsSaving(true);
          await saveDataQualityConfig({ aiSettingsValue: dqAiSettings });
          setAiSettingsSaving(false);
          setAiSettingsModalVisible(false);
          message.success('AI engine settings saved at account level');
        }}
        onCancel={() => setAiSettingsModalVisible(false)}
        title={
          <Space>
            <RobotOutlined />
            Data Quality AI Engine Settings
          </Space>
        }
        okText="Save & Close"
        okButtonProps={{ loading: aiSettingsSaving }}
        width={600}
      >
        <Alert
          message="Configure AI Engine"
          description="Set a default AI engine to use for data quality operations and configure each engine credentials/model."
          type="info"
          showIcon
          className="mb-3"
        />
        
        <Form layout="vertical">
          <Form.Item label="Default AI Engine (used by AI analysis and standardization)">
            <Select
              value={dqAiSettings.default_engine || dqAiSettings.engine || 'openai'}
              onChange={(val) => setDqAiSettings((prev) => ({ ...prev, default_engine: val }))}
              style={{ width: '100%' }}
            >
              <Option value="openai">OpenAI (GPT)</Option>
              <Option value="anthropic">Anthropic (Claude)</Option>
              <Option value="gemini">Google Gemini</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Default Taxonomy Root (for category standardization)">
            <Select
              allowClear
              placeholder="Use all active roots"
              value={dqAiSettings.taxonomy_root_id ?? undefined}
              onChange={(val) => setDqAiSettings((prev) => ({ ...prev, taxonomy_root_id: val ?? null }))}
              options={taxonomyRootOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="Engine Settings Panel">
            <Select
              value={dqAiSettings.engine}
              onChange={(val) => setDqAiSettings((prev) => ({ ...prev, engine: val }))}
              style={{ width: '100%' }}
            >
              <Option value="openai">OpenAI (GPT)</Option>
              <Option value="anthropic">Anthropic (Claude)</Option>
              <Option value="gemini">Google Gemini</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Parallel Workers for AI Standardization">
            <InputNumber
              min={1}
              max={12}
              style={{ width: '100%' }}
              value={dqAiSettings.parallel_workers}
              onChange={(val) =>
                setDqAiSettings((prev) => ({ ...prev, parallel_workers: Math.max(1, Math.min(12, Number(val || 1))) }))
              }
            />
          </Form.Item>
          <Form.Item label="Temperature for AI Calls">
            <InputNumber
              min={0}
              max={2}
              step={0.1}
              style={{ width: '100%' }}
              value={dqAiSettings.temperature}
              onChange={(val) =>
                setDqAiSettings((prev) => ({
                  ...prev,
                  temperature: Math.max(0, Math.min(2, Number(val ?? 0.1))),
                }))
              }
            />
          </Form.Item>

          {dqAiSettings.engine === 'openai' && (
            <>
              <Form.Item label="OpenAI API Key">
                <Input.Password
                  value={dqAiSettings.openai_api_key}
                  onChange={(e) => setDqAiSettings((prev) => ({ ...prev, openai_api_key: e.target.value }))}
                  placeholder="sk-..."
                />
              </Form.Item>
              <Form.Item label="OpenAI Model">
                <Input
                  value={dqAiSettings.openai_model}
                  onChange={(e) => setDqAiSettings((prev) => ({ ...prev, openai_model: e.target.value }))}
                  placeholder="gpt-4, gpt-3.5-turbo, etc."
                />
              </Form.Item>
              <Form.Item label="Max Request Tokens">
                <InputNumber
                  min={1024}
                  max={2000000}
                  style={{ width: '100%' }}
                  value={dqAiSettings.openai_max_request_tokens}
                  onChange={(val) =>
                    setDqAiSettings((prev) => ({ ...prev, openai_max_request_tokens: val || 0 }))
                  }
                />
              </Form.Item>
              <Form.Item label="Max Response Tokens">
                <InputNumber
                  min={256}
                  max={100000}
                  style={{ width: '100%' }}
                  value={dqAiSettings.openai_max_response_tokens}
                  onChange={(val) =>
                    setDqAiSettings((prev) => ({ ...prev, openai_max_response_tokens: val || 0 }))
                  }
                />
              </Form.Item>
              <Button
                type="dashed"
                onClick={() => applyTokenDefaults('openai', dqAiSettings.openai_model)}
              >
                Use model defaults
              </Button>
            </>
          )}

          {dqAiSettings.engine === 'anthropic' && (
            <>
              <Form.Item label="Anthropic Model (picker)">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a model to populate the text box"
                  loading={anthropicModelsLoading}
                  value={dqAiSettings.anthropic_model || undefined}
                  onChange={(val) =>
                    setDqAiSettings((prev) => ({ ...prev, anthropic_model: val || '' }))
                  }
                  options={anthropicModels.map((model) => ({
                    label: model.description
                      ? `${model.id} - ${model.description}`
                      : model.id,
                    value: model.id,
                  }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item label="Anthropic API Key">
                <Input.Password
                  value={dqAiSettings.anthropic_api_key}
                  onChange={(e) => setDqAiSettings((prev) => ({ ...prev, anthropic_api_key: e.target.value }))}
                  placeholder="sk-ant-..."
                />
              </Form.Item>
              <Form.Item label="Anthropic Model">
                <Input
                  value={dqAiSettings.anthropic_model}
                  onChange={(e) => setDqAiSettings((prev) => ({ ...prev, anthropic_model: e.target.value }))}
                  placeholder="claude-3-5-sonnet-20240620, etc."
                />
              </Form.Item>
              <Form.Item label="Max Request Tokens">
                <InputNumber
                  min={1024}
                  max={2000000}
                  style={{ width: '100%' }}
                  value={dqAiSettings.anthropic_max_request_tokens}
                  onChange={(val) =>
                    setDqAiSettings((prev) => ({ ...prev, anthropic_max_request_tokens: val || 0 }))
                  }
                />
              </Form.Item>
              <Form.Item label="Max Response Tokens">
                <InputNumber
                  min={256}
                  max={100000}
                  style={{ width: '100%' }}
                  value={dqAiSettings.anthropic_max_response_tokens}
                  onChange={(val) =>
                    setDqAiSettings((prev) => ({ ...prev, anthropic_max_response_tokens: val || 0 }))
                  }
                />
              </Form.Item>
              <Button
                type="dashed"
                onClick={() => applyTokenDefaults('anthropic', dqAiSettings.anthropic_model)}
              >
                Use model defaults
              </Button>
            </>
          )}

          {dqAiSettings.engine === 'gemini' && (
            <>
              <Form.Item label="Google Gemini API Key">
                <Input.Password
                  value={dqAiSettings.gemini_api_key}
                  onChange={(e) => setDqAiSettings((prev) => ({ ...prev, gemini_api_key: e.target.value }))}
                  placeholder="AIza..."
                />
              </Form.Item>
              <Form.Item label="Gemini Model">
                <Input
                  value={dqAiSettings.gemini_model}
                  onChange={(e) => setDqAiSettings((prev) => ({ ...prev, gemini_model: e.target.value }))}
                  placeholder="gemini-pro, gemini-1.5-pro, etc."
                />
              </Form.Item>
              <Form.Item label="Max Request Tokens">
                <InputNumber
                  min={1024}
                  max={2000000}
                  style={{ width: '100%' }}
                  value={dqAiSettings.gemini_max_request_tokens}
                  onChange={(val) =>
                    setDqAiSettings((prev) => ({ ...prev, gemini_max_request_tokens: val || 0 }))
                  }
                />
              </Form.Item>
              <Form.Item label="Max Response Tokens">
                <InputNumber
                  min={256}
                  max={100000}
                  style={{ width: '100%' }}
                  value={dqAiSettings.gemini_max_response_tokens}
                  onChange={(val) =>
                    setDqAiSettings((prev) => ({ ...prev, gemini_max_response_tokens: val || 0 }))
                  }
                />
              </Form.Item>
              <Button
                type="dashed"
                onClick={() => applyTokenDefaults('gemini', dqAiSettings.gemini_model)}
              >
                Use model defaults
              </Button>
            </>
          )}
        </Form>

        <Divider />
        <Space className="d-flex justify-content-between w-100" align="center">
          <Text type="secondary">
            Default engine: <Tag color="blue">{dqAiSettings.default_engine || dqAiSettings.engine || 'openai'}</Tag>
          </Text>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={testingAiEngine}
            onClick={testAiEngine}
          >
            Test Connection
          </Button>
        </Space>
      </Modal>

      <style>{`
        .field-card:hover {
          border-color: #1890ff !important;
        }
        .field-card.selected {
          background: #e6f7ff;
        }
      `}</style>
    </div>
  );
};

export default DataQuality;
